// Time Tracking module for Docospot
// Handles time tracking events, filtering, and reports

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { API_BASE_URL } from '../core/config.js';
import { showError, showSuccess } from '../core/ui.js';
import { updatePageTitle, updateActiveNavigation } from './navigation.js';
import { getCurrentUser, formatDateTime, getEffectiveAccountId } from '../utils/helpers.js';
import { createCalendarPicker } from '../utils/calendar-picker.js';

/**
 * Format a Date to YYYY-MM-DD using local time (not UTC) to avoid timezone shifts.
 */
function formatLocalDate(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Convert a YYYY-MM-DD string to a UTC ISO string representing local midnight.
 * Appending T00:00:00 (no Z) makes the JS Date constructor use local time.
 */
function localDateToStartISO(dateStr) {
    return new Date(dateStr + 'T00:00:00').toISOString();
}

/**
 * Convert a YYYY-MM-DD string to a UTC ISO string representing local end-of-day (23:59:59.999).
 */
function localDateToEndISO(dateStr) {
    return new Date(dateStr + 'T23:59:59.999').toISOString();
}

/**
 * Format the location time budget for display in the events table.
 * Returns a string like "8h / day" / "40h / week" / "160h / month",
 * or an em-dash when no budget is configured for the location.
 */
function formatLocationTimeBudget(event) {
    const hours = event && event.location_time_budget_hours;
    if (hours == null || hours === '' || isNaN(Number(hours))) {
        return '<span class="text-gray-400">–</span>';
    }
    const period = event.location_time_budget_period;
    const periodLabel = period === 'daily' ? t('time.budgetDaily')
        : period === 'weekly' ? t('time.budgetWeekly')
        : period === 'monthly' ? t('time.budgetMonthly')
        : '';
    const num = Number(hours);
    const totalMinutes = Math.round(num * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    const display = `${h}h ${m}m`;
    return periodLabel ? `${display} / ${periodLabel}` : display;
}
import { apiCall } from '../core/api.js';
import './time-tracking-modals.js'; // Import modals to make functions available globally

// Time tracking state
export const timeTrackingState = {
    currentPage: 1,
    pageSize: 50,
    allEvents: [],
    totalCount: 0,
    hasMore: true,
    isLoading: false,
    viewMode: 'current', // 'current' or 'original'
    smartTimeEnabled: false,
    smartTimeSettingsLoaded: false,
    smartTimeSettings: {
        minimumDeviationMinutes: 0,
        randomDeviationMinutes: 0
    },
    smartTimeLocationCache: new Map(),
    smartTimeOpenEvents: new Set(),
    pendingHighlightEventId: null,
    pendingHighlightTimer: null,
    filters: {
        dateFrom: null,
        dateTo: null,
        status: '',
        userSearch: '',
        accountId: null,
        locationId: null
    }
};

// Permissions stub (simplified for now)
const permissions = {
    can: (resource, action) => {
        const currentUser = getCurrentUser();
        return currentUser && (currentUser.role === 'Administrator' || currentUser.role === 'SuperAdmin');
    }
};

/**
 * Show Time Tracking main page with tabs
 */
export async function showTimeTracking() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    if (!auth.isAuthenticated()) {
        showError('Please login to view time tracking');
        return;
    }

    // Update page title with tabs
    const pageTitleElement = document.getElementById('page-title');
    if (pageTitleElement) {
        const canAccessTimeBudget = window.permissions && window.permissions.canAccessDataMenu('timeBudget');
        pageTitleElement.innerHTML = `
            <div class="flex items-center space-x-8">
                <button onclick="window.showTimeTrackingTab()" 
                        id="time-tracking-tab"
                        class="text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold">
                    ${t('time.timeTracking')}
                </button>
                ${canAccessTimeBudget ? `<button onclick="window.showTimeBudgetTab()" 
                        id="time-budget-tab"
                        class="text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium">
                    ${t('time.timeBudget')}
                </button>` : ''}
            </div>
        `;
    }

    mainContent.innerHTML = `
        <!-- Tab Content Container -->
        <div id="time-tab-content">
            <!-- Content will be loaded here -->
        </div>
    `;

    // Show the time tracking tab by default
    showTimeTrackingTab();
}

/**
 * Show Time Tracking tab content
 */
export async function showTimeTrackingTab() {
    const tabContent = document.getElementById('time-tab-content');
    if (!tabContent) return;

    // Update tab styling
    const timeTrackingTab = document.getElementById('time-tracking-tab');
    const timeBudgetTab = document.getElementById('time-budget-tab');
    
    if (timeTrackingTab && timeBudgetTab) {
        timeTrackingTab.className = 'text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold';
        timeBudgetTab.className = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
    }

    const currentUser = getCurrentUser();
    const isExternalContact = currentUser && currentUser.role === 'External contact';
    const tableColspan = isExternalContact ? 6 : 8;

    // Bookkeepers are read-only viewers of original (sensor) registrations — force original view
    if (currentUser && currentUser.role === 'Bookkeeper') {
        timeTrackingState.viewMode = 'original';
    }

    tabContent.innerHTML = `
        <!-- Filter Row - Sticky -->
        <div class="sticky top-16 z-30 mb-0 bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3">
            <div class="bg-gray-200 shadow rounded-lg p-4">
                <!-- Export, Import, and Add Event Buttons -->
                <div class="flex justify-end gap-2 mb-2">
                    <button onclick="generateTimeTrackingPDF()"
                            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-sm">
                        <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        PDF
                    </button>
                    <button onclick="showTimeTrackingExportModal()"
                            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-sm">
                        <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export to Excel
                    </button>
                    ${permissions.can('timeTracking', 'create') ? `
                    <button onclick="openImportEventsModal()"
                            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-sm">
                        <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Import Events
                    </button>
                    <button onclick="openAddEventModal()"
                            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm">
                        <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        ${t('time.addEvent')}
                    </button>
                    ` : ''}
                </div>
                
                <!-- View Toggle and Date Range -->
                <div class="flex items-end gap-4 mb-3">
                    ${currentUser && (currentUser.role === 'Administrator' || currentUser.role === 'SuperAdmin' || currentUser.role === 'Team leader') ? `
                    <!-- View Toggle (Original vs Current) -->
                    <div class="flex-shrink-0">
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('time.viewMode')}</label>
                        <div class="flex rounded-md shadow-sm">
                            <button type="button" 
                                    id="view-current-btn"
                                    onclick="toggleTimeView('current')"
                                    class="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-primary-600 text-sm font-medium text-white hover:bg-primary-700 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500">
                                ${t('time.current')}
                            </button>
                            <button type="button" 
                                    id="view-original-btn"
                                    onclick="toggleTimeView('original')"
                                    class="relative -ml-px inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500">
                                ${t('time.original')}
                            </button>
                        </div>
                    </div>
                    ` : ''}
                    
                    <!-- Date Range Picker -->
                    <div class="flex-1">
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('time.dateRange')}</label>
                        <button id="time-tracking-date-range-button" 
                                type="button"
                                class="invisible relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm">
                            <span id="time-tracking-date-range-display" class="block truncate text-gray-400">Select date range</span>
                            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clip-rule="evenodd" fill-rule="evenodd"/>
                                </svg>
                            </span>
                        </button>
                        <!-- Hidden inputs for form compatibility -->
                        <input type="hidden" id="time-tracking-date-from">
                        <input type="hidden" id="time-tracking-date-to">
                        <!-- Calendar Picker Popup (initially hidden with opacity-0 to prevent flash) -->
                        <div id="time-tracking-calendar-popup" class="hidden opacity-0 fixed z-50 bg-white rounded-lg shadow-lg ring-1 ring-gray-200 p-4" style="width: 320px;"></div>
                    </div>
                    
                    <!-- Status Filter -->
                    <div class="w-40">
                        <label for="time-tracking-status-filter" class="block text-sm font-medium text-gray-700 mb-1">${t('time.status')}</label>
                        <select id="time-tracking-status-filter" 
                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2"
                                onchange="filterTimeTracking()">
                            <option value="">${t('time.allStatuses')}</option>
                            <option value="closed">${t('time.completed')}</option>
                            <option value="open">${t('time.open')}</option>
                            ${currentUser && (currentUser.role === 'Administrator' || currentUser.role === 'SuperAdmin' || currentUser.role === 'Team leader') ? `<option value="modified">${t('time.modified')}</option>` : ''}
                        </select>
                    </div>
                    
                    <!-- Action buttons -->
                    <div class="flex items-end space-x-2">
                        <button onclick="clearTimeTrackingFilters()" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            ${t('common.clear')}
                        </button>
                        <button onclick="loadTimeTrackingData(true)" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            ${t('common.refresh')}
                        </button>
                    </div>
                </div>
                
                <!-- Row 3: User Search, Account, and Location -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <!-- User Search (multi-select) -->
                    <div class="relative">
                        <label for="time-tracking-user-search" class="block text-sm font-medium text-gray-700 mb-1">${t('time.searchUser')}</label>
                        <div id="time-tracking-user-chips" class="flex flex-wrap gap-1 mb-1"></div>
                        <input type="text"
                               id="time-tracking-user-search"
                               placeholder="${t('time.searchUserPlaceholder')}"
                               autocomplete="off"
                               class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                        <div id="time-tracking-user-results"
                             class="hidden absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        </div>
                        <label class="flex items-center cursor-pointer mt-1">
                            <input type="checkbox" id="time-tracking-unknown-user-checkbox"
                                   onchange="toggleUnknownUserFilter()"
                                   class="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400">
                            <span class="ml-2 text-xs font-medium text-orange-600">${t('time.unknownUserFilter') || 'Show only Unknown Users'}</span>
                        </label>
                    </div>
                    
                    <!-- Account Filter -->
                    <div class="relative">
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('time.account')}</label>
                        <button type="button" id="time-tracking-account-toggle" 
                                onclick="toggleTimeTrackingAccountTree()"
                                class="block w-full text-left rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                            <span id="time-tracking-account-selected">${t('time.allAccounts')}</span>
                            <svg class="absolute right-2 top-9 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                        </button>
                        <div id="time-tracking-account-tree-container" class="hidden absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-w-md">
                            <div id="time-tracking-account-tree" class="p-2 max-h-64 overflow-y-auto">
                                <!-- Tree will be populated here -->
                            </div>
                        </div>
                    </div>
                    
                    <!-- Location Filter -->
                    <div class="relative">
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('time.location')}</label>
                        <button type="button" id="time-tracking-location-toggle" 
                                onclick="toggleTimeTrackingLocationDropdown()"
                                class="block w-full text-left rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                            <span id="time-tracking-location-selected">${t('time.allLocations')}</span>
                            <svg class="absolute right-2 top-9 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                        </button>
                        <div id="time-tracking-location-dropdown" class="hidden absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                            <div class="p-2 border-b border-gray-200">
                                <label class="flex items-center cursor-pointer py-1 px-1">
                                    <input type="checkbox" id="time-tracking-unknown-location-checkbox"
                                           onchange="toggleUnknownLocationFilter()"
                                           class="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400">
                                    <span class="ml-2 text-sm font-medium text-orange-600">Unknown location</span>
                                </label>
                            </div>
                            <div class="p-2 border-b border-gray-200">
                                <input type="text" id="time-tracking-location-search" 
                                       placeholder="${t('time.searchLocations')}"
                                       autocomplete="off"
                                       class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                            </div>
                            <div id="time-tracking-location-list" class="max-h-60 overflow-y-auto p-2">
                                <!-- Locations will appear here -->
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Count Display -->
        <div class="bg-white shadow rounded-lg mt-3 px-6 py-3">
            <div id="time-tracking-count-display" class="text-sm text-gray-600">
                <span class="font-medium">${t('time.loadingEvents')}</span>
            </div>
        </div>
        
        <!-- Time Tracking List -->
        <div class="bg-white shadow rounded-lg mt-3 overflow-hidden">
            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-200">
                        <tr>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('time.date')}
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('time.employee')}
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('time.location')}
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('time.checkIn')}
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('time.checkOut')}
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('time.totalTime')}
                            </th>
                            ${isExternalContact ? '' : `
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('time.timeBudget')}
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('common.actions')}
                            </th>
                            `}
                        </tr>
                    </thead>
                    <tbody id="time-tracking-list" class="bg-white divide-y divide-gray-200">
                        <tr>
                            <td colspan="${tableColspan}" class="px-6 py-4 text-center">
                                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                                <p class="mt-2 text-sm text-gray-500">${t('time.loadingEvents')}</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        
        <!-- Loading indicator for infinite scroll -->
        <div id="time-tracking-loading-more" class="hidden mt-4 text-center py-4 bg-white shadow rounded-lg">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-2 text-sm text-gray-500">Loading more events...</p>
        </div>
        
        <!-- End of list indicator -->
        <div id="time-tracking-end-message" class="hidden mt-4 text-center py-3 text-sm text-gray-500">
            All events loaded
        </div>
    `;

    // Note: Page title is set by showTimeTracking() with tab buttons
    updateActiveNavigation('time-tracking');
    
    // Set default date range: 1 month back to today
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    
    const dateFromInput = document.getElementById('time-tracking-date-from');
    const dateToInput = document.getElementById('time-tracking-date-to');
    
    // Set values BEFORE creating calendar picker to prevent flash
    if (dateFromInput) {
        dateFromInput.value = formatLocalDate(oneMonthAgo);
        timeTrackingState.filters.dateFrom = dateFromInput.value;
    }
    if (dateToInput) {
        dateToInput.value = formatLocalDate(today);
        timeTrackingState.filters.dateTo = dateToInput.value;
    }
    
    // Pre-format the display text to avoid flash
    const dateDisplay = document.getElementById('time-tracking-date-range-display');
    if (dateDisplay) {
        const formatDate = (d) => {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
        };
        dateDisplay.textContent = `${formatDate(oneMonthAgo)} - ${formatDate(today)}`;
        dateDisplay.classList.remove('text-gray-400');
        dateDisplay.classList.add('text-gray-700');
    }

    // Initialize custom calendar picker for date range (it will read from inputs)
    const calendarPicker = createCalendarPicker({
        inputId: 'time-tracking-date-from',
        buttonId: 'time-tracking-date-range-button',
        displayId: 'time-tracking-date-range-display',
        popupId: 'time-tracking-calendar-popup',
        isRange: true,
        defaultDate: oneMonthAgo,
        onChange: (dates) => {
            const timestamp = new Date().toISOString();
            console.log(`[${timestamp}] Time Tracker calendar onChange triggered:`, dates);
            if (dates && dates.startDate && dates.endDate) {
                // Update hidden inputs with selected dates
                const fromInput = document.getElementById('time-tracking-date-from');
                const toInput = document.getElementById('time-tracking-date-to');
                
                if (fromInput) fromInput.value = formatLocalDate(dates.startDate);
                if (toInput) toInput.value = formatLocalDate(dates.endDate);
                
                // Update state and trigger filter
                timeTrackingState.filters.dateFrom = formatLocalDate(dates.startDate);
                timeTrackingState.filters.dateTo = formatLocalDate(dates.endDate);
                
                console.log('Loading time tracking data with filters:', timeTrackingState.filters);
                // Load data with new filters (reset to page 1)
                loadTimeTrackingData(true);
            }
        }
    });

    // Store picker instance globally for calendar buttons to access
    window.calendarPickers = window.calendarPickers || {};
    window.calendarPickers['time-tracking-calendar-popup'] = calendarPicker;

    // Remove opacity-0 after initialization to prevent flash on page load
    const calendarPopup = document.getElementById('time-tracking-calendar-popup');
    if (calendarPopup) {
        calendarPopup.classList.remove('opacity-0');
    }
    
    // Make date range button visible after initialization (display text already set above)
    const dateRangeButton = document.getElementById('time-tracking-date-range-button');
    if (dateRangeButton) {
        dateRangeButton.classList.remove('invisible');
        // Close account/location dropdowns when opening the date picker
        dateRangeButton.addEventListener('click', () => {
            document.getElementById('time-tracking-account-tree-container')?.classList.add('hidden');
            document.getElementById('time-tracking-location-dropdown')?.classList.add('hidden');
        });
    }
    
    // Setup location search functionality
    setupTimeTrackingLocationSearch();
    
    // Setup user multi-select search functionality
    setupTimeTrackingUserSearch();
    
    // Setup click outside handlers for dropdowns
    setupTimeTrackingDropdownHandlers();
    
    // Setup infinite scroll
    setupTimeTrackingInfiniteScroll();
    
    // Load accounts for filter
    await loadAccountsForTimeTracking();
    
    // Load initial data with default date range
    console.log('Loading initial time tracking data...');
    await loadTimeTrackingData(true);
    console.log('Initial data load complete');
}

/**
 * Setup click outside handlers for account and location dropdowns
 */
function setupTimeTrackingDropdownHandlers() {
    document.addEventListener('click', function(event) {
        // Close account tree when clicking outside
        const accountContainer = document.getElementById('time-tracking-account-tree-container');
        const accountToggle = document.getElementById('time-tracking-account-toggle');
        
        if (accountContainer && accountToggle && 
            !accountContainer.contains(event.target) && 
            !accountToggle.contains(event.target)) {
            accountContainer.classList.add('hidden');
        }
        
        // Close location dropdown when clicking outside
        const locationDropdown = document.getElementById('time-tracking-location-dropdown');
        const locationToggle = document.getElementById('time-tracking-location-toggle');
        
        if (locationDropdown && locationToggle && 
            !locationDropdown.contains(event.target) && 
            !locationToggle.contains(event.target)) {
            locationDropdown.classList.add('hidden');
        }

        // Close user search results when clicking outside
        const userResults = document.getElementById('time-tracking-user-results');
        const userInput = document.getElementById('time-tracking-user-search');
        if (userResults && userInput &&
            !userResults.contains(event.target) &&
            !userInput.contains(event.target)) {
            userResults.classList.add('hidden');
            userResults.innerHTML = '';
            userInput.value = '';
        }
    });
}

/**
 * Setup infinite scroll for time tracking
 */
function setupTimeTrackingInfiniteScroll() {
    let scrollTimeout;
    window.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
            const scrollPosition = window.innerHeight + window.scrollY;
            const documentHeight = document.documentElement.scrollHeight;
            const threshold = 300;
            
            if (scrollPosition >= documentHeight - threshold) {
                if (timeTrackingState.hasMore && !timeTrackingState.isLoading) {
                    loadTimeTrackingData(false);
                }
            }
        }, 100);
    });
}

/**
 * Load time tracking data
 */
export async function loadTimeTrackingData(reset = false) {
    if (timeTrackingState.isLoading || (!reset && !timeTrackingState.hasMore)) {
        return;
    }

    timeTrackingState.isLoading = true;

    if (reset) {
        timeTrackingState.currentPage = 1;
        timeTrackingState.allEvents = [];
        timeTrackingState.hasMore = true;
    }

    if (!reset) {
        const loadingMore = document.getElementById('time-tracking-loading-more');
        if (loadingMore) loadingMore.classList.remove('hidden');
    }

    try {
        const skip = (timeTrackingState.currentPage - 1) * timeTrackingState.pageSize;
        const params = new URLSearchParams({
            skip: skip.toString(),
            limit: timeTrackingState.pageSize.toString()
        });

        // Add filters
        if (timeTrackingState.filters.dateFrom) {
            params.append('start_date', localDateToStartISO(timeTrackingState.filters.dateFrom));
        }
        if (timeTrackingState.filters.dateTo) {
            params.append('end_date', localDateToEndISO(timeTrackingState.filters.dateTo));
        }
        if (timeTrackingState.filters.status) {
            params.append('status', timeTrackingState.filters.status);
        }
        if (selectedTimeTrackingUsers.size > 0) {
            selectedTimeTrackingUsers.forEach((_name, userId) => {
                params.append('user_id', userId);
            });
        }
        
        // Add account filter if any accounts are selected
        if (selectedTimeTrackingAccounts.size > 0) {
            const accountIds = [];
            selectedTimeTrackingAccounts.forEach(accountId => {
                const idsWithDescendants = getAllAccountIdsIncludingDescendants(accountId);
                accountIds.push(...idsWithDescendants);
            });
            // Remove duplicates
            const uniqueAccountIds = [...new Set(accountIds)];
            uniqueAccountIds.forEach(id => params.append('account_id', id));
            console.log('[TIME TRACKING] Account filter:', Array.from(selectedTimeTrackingAccounts), 'expanded to:', uniqueAccountIds);
        }
        
        // Add location filter if any locations are selected
        if (selectedTimeTrackingLocations.size > 0) {
            selectedTimeTrackingLocations.forEach(locationId => {
                params.append('location_id', locationId);
            });
            console.log('[TIME TRACKING] Location filter:', Array.from(selectedTimeTrackingLocations));
        }

        // Unknown location filter overrides account/location filters
        if (filterUnknownLocation) {
            params.append('unknown_location', 'true');
        }

        // Unknown user filter (events where the NFC card was not yet registered)
        if (filterUnknownUser) {
            params.append('unknown_user', 'true');
        }

        console.log('Fetching time tracking with params:', params.toString());
        const response = await fetch(`${API_BASE_URL}/time-tracking/events/?${params}`, {
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });

        if (!response.ok) {
            throw new Error('Failed to load time tracking events');
        }

        const data = await response.json();
        const newEvents = data.events || [];
        const totalCount = data.total || 0;
        console.log('Received events:', newEvents.length, 'Total:', totalCount);
        
        timeTrackingState.totalCount = totalCount;
        timeTrackingState.allEvents.push(...newEvents);

        if (timeTrackingState.allEvents.length >= totalCount || newEvents.length < timeTrackingState.pageSize) {
            timeTrackingState.hasMore = false;
        }

        const loadingMore = document.getElementById('time-tracking-loading-more');
        if (loadingMore) loadingMore.classList.add('hidden');

        const endMessage = document.getElementById('time-tracking-end-message');
        if (endMessage) {
            if (timeTrackingState.hasMore) {
                endMessage.classList.add('hidden');
            } else if (timeTrackingState.allEvents.length > 0) {
                endMessage.classList.remove('hidden');
            }
        }

        if (timeTrackingState.smartTimeEnabled) {
            await ensureSmartTimeDataForEvents(timeTrackingState.allEvents);
        }

        console.log('Calling displayTimeTrackingEvents with', timeTrackingState.allEvents.length, 'events');
        displayTimeTrackingEvents(timeTrackingState.allEvents);
        // Re-apply highlight if a save just happened (survives WS-triggered reloads)
        if (timeTrackingState.pendingHighlightEventId) {
            applyRowHighlight(timeTrackingState.pendingHighlightEventId);
        }
        updateTimeTrackingCount();
        timeTrackingState.currentPage++;
        timeTrackingState.isLoading = false;

    } catch (error) {
        timeTrackingState.isLoading = false;
        console.error('Error loading time tracking:', error);
        showError('Failed to load time tracking events: ' + (error.message || 'Unknown error'));
        console.error('Error loading time tracking:', error);

        const loadingMore = document.getElementById('time-tracking-loading-more');
        if (loadingMore) loadingMore.classList.add('hidden');
    }
}

/**
 * Apply a temporary yellow highlight to the row for the given eventId.
 * Clears any existing highlight timer before setting a new one.
 */
function applyRowHighlight(eventId) {
    const rows = document.querySelectorAll('#time-tracking-list tr');
    rows.forEach(row => {
        const spans = row.querySelectorAll('span[data-event-id]');
        let isMatch = false;
        spans.forEach(span => { if (span.getAttribute('data-event-id') === eventId) isMatch = true; });
        if (isMatch) {
            row.classList.remove('transition-colors', 'duration-1000');
            row.classList.add('bg-yellow-100');
        }
    });
}

/**
 * Display time tracking events
 */
function displayTimeTrackingEvents(events) {
    console.log('displayTimeTrackingEvents called with', events.length, 'events');
    const listElement = document.getElementById('time-tracking-list');
    if (!listElement) {
        console.error('time-tracking-list element not found!');
        return;
    }
    console.log('Found time-tracking-list element, rendering...');

    const viewOriginal = timeTrackingState.viewMode === 'original';
    const currentUserForView = getCurrentUser();
    const isExternalContact = currentUserForView && currentUserForView.role === 'External contact';
    const tableColspan = isExternalContact ? 6 : 8;

    // In Original mode, exclude manually created events — they have no sensor origin
    const eventsToRender = viewOriginal ? events.filter(e => !e.is_manual) : events;

    if (eventsToRender.length === 0) {
        listElement.innerHTML = `
            <tr>
                <td colspan="${tableColspan}" class="px-6 py-8 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900">No sensor events found</h3>
                    <p class="mt-1 text-sm text-gray-500">All events in this range were manually created.</p>
                </td>
            </tr>
        `;
        return;
    }

    listElement.innerHTML = eventsToRender.map(event => {
        const userName = event.user_name || event.username || 'Unknown User';
        const isUnknownUser = userName === 'Unknown User';
        const locationName = event.location_name || 'Unknown Location';
        const isUnknownLocation = locationName === 'Unknown Location';
        
        // Use original or current values based on view mode
        // viewOriginal already defined above
        
        // Format date from check-in time
        const checkInDate = event.check_in_time ? new Date(viewOriginal && event.original_check_in_time ? event.original_check_in_time : event.check_in_time).toLocaleDateString('da-DK', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }) : '-';
        
        // Format times as HH:MM only
        const checkInTime = (viewOriginal && event.original_check_in_time ? event.original_check_in_time : event.check_in_time) ? 
            new Date(viewOriginal && event.original_check_in_time ? event.original_check_in_time : event.check_in_time).toLocaleTimeString('da-DK', {
                hour: '2-digit',
                minute: '2-digit'
            }) : '-';
        const checkOutTime = (viewOriginal && event.original_check_out_time ? event.original_check_out_time : event.check_out_time) ? 
            new Date(viewOriginal && event.original_check_out_time ? event.original_check_out_time : event.check_out_time).toLocaleTimeString('da-DK', {
                hour: '2-digit',
                minute: '2-digit'
            }) : '-';
        
        // Calculate duration from duration_minutes (same as Time Budget)
        let totalTime = '-';
        const durationMinutes = viewOriginal && event.original_duration_minutes !== null && event.original_duration_minutes !== undefined ?
            event.original_duration_minutes :
            event.duration_minutes;
            
        if (durationMinutes) {
            const hours = Math.floor(durationMinutes / 60);
            const minutes = Math.round(durationMinutes % 60);
            totalTime = `${hours}h ${minutes}m`;
        }
        
        const isOpen = viewOriginal ? 
            !(event.original_check_out_time) : 
            !(event.check_out_time);
        
        // Determine color for username based on status
        const userNameColor = event.status === 'closed' ? 
            'text-green-600 font-medium' : 
            'text-yellow-600 font-medium';
        
        // Check if user can edit (Administrator or Team leader)
        const currentUser = getCurrentUser();
        const canEdit = currentUser && (currentUser.role === 'Administrator' || currentUser.role === 'SuperAdmin' || currentUser.role === 'Team leader');
        
        // Status indicator dots (only visible to admin/team leader)
        let statusDot = '';
        if (canEdit && event.is_modified) {
            // Orange dot for modified
            statusDot = `<span onclick="viewEditHistory('${event._id}')" class="inline-block w-2 h-2 rounded-full bg-orange-500 mr-2 cursor-pointer hover:ring-2 hover:ring-orange-300" title="${t('time.modified')}"></span>`;
        } else if (canEdit && event.is_manual) {
            // Blue dot for manual
            statusDot = `<span onclick="viewEditHistory('${event._id}')" class="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2 cursor-pointer hover:ring-2 hover:ring-blue-300" title="${t('time.manual')}"></span>`;
        }
        
        // Make timestamps clickable if user can edit
        const checkInClass = canEdit ? 'cursor-pointer hover:text-blue-600 hover:underline' : '';
        const checkOutClass = canEdit ? 'cursor-pointer hover:text-blue-600 hover:underline' : '';
        const editDataAttr = canEdit ? `data-event-id="${event._id}"` : '';
        
        // Make unknown user clickable for admins
        const userNameClass = isUnknownUser && canEdit ? 
            'cursor-pointer hover:text-red-700 hover:underline' : 
            '';
        const userNameOnClick = isUnknownUser && canEdit ? 
            `onclick="handleUnknownUser('${event._id}', '${event.card_id}')"` : 
            '';
        
        // Make unknown location clickable for admins
        const locationNameClass = isUnknownLocation && canEdit ? 
            'cursor-pointer hover:text-red-700 hover:underline' : 
            '';
        const locationNameOnClick = isUnknownLocation && canEdit ? 
            `onclick="handleUnknownLocation('${event._id}', '${event.sensor_id}')"` : 
            '';

        const smartTimeSuggestion = (timeTrackingState.smartTimeEnabled && canEdit)
            ? getSmartTimeSuggestion(event)
            : null;
        const isSmartTimeExpanded = smartTimeSuggestion && timeTrackingState.smartTimeOpenEvents.has(event._id);

        return `
            <tr class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${checkInDate}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm ${userNameColor}">
                    <div class="flex items-center">
                        ${statusDot}
                        <span class="${userNameClass}" ${userNameOnClick}>${userName}</span>
                    </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span class="${locationNameClass}" ${locationNameOnClick}>${locationName}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span class="${checkInClass}" ${editDataAttr} data-field="check_in_time" onclick="${canEdit ? 'openEditTimeModal(this)' : ''}">${checkInTime}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span class="${checkOutClass}" ${editDataAttr} data-field="check_out_time" onclick="${canEdit ? 'openEditTimeModal(this)' : ''}">${checkOutTime}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ${totalTime}
                </td>
                ${isExternalContact ? '' : `
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    ${formatLocationTimeBudget(event)}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    ${canEdit ? `
                    <div class="grid grid-cols-4 items-center gap-2">
                        <button onclick="event.stopPropagation(); deleteTimeTrackingEvent('${event._id}')" 
                                class="text-red-600 hover:text-red-800" 
                                title="${t('common.delete')}">
                            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                        <button onclick="event.stopPropagation(); copyTimeTrackingEvent('${event._id}')" 
                                class="text-blue-600 hover:text-blue-800" 
                                title="${t('common.copy')}">
                            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                        </button>
                        ${event.is_manual || event.is_modified ? `
                        <button onclick="event.stopPropagation(); editTimeTrackingEvent('${event._id}')" 
                                class="text-gray-600 hover:text-gray-800" 
                                title="${t('common.edit')}">
                            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </button>
                        ` : '<span></span>'}
                        ${smartTimeSuggestion ? `
                        <button onclick="event.stopPropagation(); toggleSmartTimeRow('${event._id}')" 
                                class="text-purple-600 hover:text-purple-800" 
                                title="SmartTime">
                            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </button>
                        ` : '<span></span>'}
                    </div>
                    ` : ''}
                </td>
                `}
            </tr>
            ${isSmartTimeExpanded ? `
            <tr class="bg-purple-50">
                <td colspan="8" class="px-6 py-3 text-sm text-gray-700">
                    <div class="space-y-2">
                        <div class="font-medium text-gray-800">SmartTime suggestions</div>
                        <div class="flex flex-wrap items-center justify-between gap-3">
                            <div class="text-sm text-gray-600">
                                Adjust check-out: <span class="font-medium">${smartTimeSuggestion.checkOut.display}</span>
                                <span class="text-gray-400">•</span>
                                Duration: ${smartTimeSuggestion.checkOut.durationDisplay}
                            </div>
                            <div class="flex items-center gap-2">
                                <button onclick="event.stopPropagation(); acceptSmartTimeSuggestion('${event._id}', 'check_out')"
                                        class="w-44 px-3 py-1.5 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 text-center">
                                    Accept check-out
                                </button>
                            </div>
                        </div>
                        ${smartTimeSuggestion.checkIn ? `
                        <div class="flex flex-wrap items-center justify-between gap-3">
                            <div class="text-sm text-gray-600">
                                Adjust check-in: <span class="font-medium">${smartTimeSuggestion.checkIn.display}</span>
                                <span class="text-gray-400">•</span>
                                Duration: ${smartTimeSuggestion.checkIn.durationDisplay}
                            </div>
                            <div class="flex items-center gap-2">
                                <button onclick="event.stopPropagation(); acceptSmartTimeSuggestion('${event._id}', 'check_in')"
                                        class="w-44 px-3 py-1.5 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 text-center">
                                    Accept check-in
                                </button>
                            </div>
                        </div>
                        ` : ''}
                    </div>
                </td>
            </tr>
            ` : ''}
        `;
    }).join('');
}

/**
 * Update count display
 */
function updateTimeTrackingCount() {
    const countElement = document.getElementById('time-tracking-count-display');
    if (!countElement) return;
    
    const displayedCount = timeTrackingState.allEvents.length;
    const totalCount = timeTrackingState.totalCount;
    
    const hasFilters = timeTrackingState.filters.status || 
                      selectedTimeTrackingUsers.size > 0 ||
                      timeTrackingState.filters.dateFrom ||
                      timeTrackingState.filters.dateTo;
    
    // External Contact role has limited access and must not see the SmartTime
    // toggle or its suggestion summary. Bookkeepers are read-only viewers of
    // original registrations and must not see SmartTime either. SmartTime is also
    // hidden when the account has turned the feature off.
    const currentUser = getCurrentUser();
    const isExternalContact = currentUser && currentUser.role === 'External contact';
    const smartTimeFeatureOff = window.permissions && window.permissions.isFeatureDisabled('smarttime');
    const hideSmartTime = isExternalContact || (currentUser && currentUser.role === 'Bookkeeper') || smartTimeFeatureOff;

    const smartTimeSuggestionCount = timeTrackingState.smartTimeEnabled
        ? timeTrackingState.allEvents.reduce((count, event) => {
            return getSmartTimeSuggestion(event) ? count + 1 : count;
        }, 0)
        : 0;

    const smartTimeInfo = (timeTrackingState.smartTimeEnabled && !hideSmartTime)
        ? `<span class="text-purple-600">SmartTime: ${smartTimeSuggestionCount} suggestion${smartTimeSuggestionCount === 1 ? '' : 's'}</span>`
        : '';

    const smartTimeToggle = hideSmartTime ? '' : `
                <label class="inline-flex items-center cursor-pointer">
                    <span class="mr-2 text-sm text-gray-600">SmartTime</span>
                    <div class="relative">
                        <input type="checkbox" class="sr-only peer" ${timeTrackingState.smartTimeEnabled ? 'checked' : ''}
                               onchange="toggleSmartTimeSuggestions(this.checked)">
                        <div class="w-10 h-6 bg-gray-200 rounded-full peer-checked:bg-primary-600 transition-colors"></div>
                        <div class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-4"></div>
                    </div>
                </label>`;

    if (hasFilters) {
        countElement.innerHTML = `
            <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="flex items-center space-x-2">
                    <span class="font-medium">${t('common.showing')} ${displayedCount.toLocaleString()} ${t('common.of')} ${totalCount.toLocaleString()} events</span>
                    <span class="text-gray-400">•</span>
                    <span class="text-blue-600">filtered</span>
                    ${smartTimeInfo ? `<span class="text-gray-400">•</span>${smartTimeInfo}` : ''}
                </div>
                ${smartTimeToggle}
            </div>
        `;
    } else {
        const loadingMore = displayedCount < totalCount && timeTrackingState.hasMore;
        countElement.innerHTML = `
            <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="flex items-center space-x-2">
                    <span class="font-medium">${t('common.total')}: ${totalCount.toLocaleString()} events</span>
                    ${loadingMore ? `<span class="text-gray-400">• <span class="italic">showing first ${displayedCount.toLocaleString()}, scroll for more</span></span>` : ''}
                    ${smartTimeInfo ? `<span class="text-gray-400">•</span>${smartTimeInfo}` : ''}
                </div>
                ${smartTimeToggle}
            </div>
        `;
    }
}

async function toggleSmartTimeSuggestions(enabled) {
    timeTrackingState.smartTimeEnabled = !!enabled;
    if (!enabled) {
        timeTrackingState.smartTimeOpenEvents = new Set();
    } else {
        await ensureSmartTimeDataForEvents(timeTrackingState.allEvents);
    }
    displayTimeTrackingEvents(timeTrackingState.allEvents);
    updateTimeTrackingCount();
}

async function ensureSmartTimeSettings() {
    if (timeTrackingState.smartTimeSettingsLoaded) return;
    try {
        const settings = await apiCall('/settings', {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        timeTrackingState.smartTimeSettings = {
            minimumDeviationMinutes: settings.smart_time_minimum_deviation_minutes ?? 0,
            randomDeviationMinutes: settings.smart_time_random_deviation_minutes ?? 0
        };
        timeTrackingState.smartTimeSettingsLoaded = true;
    } catch (error) {
        console.error('Failed to load SmartTime settings:', error);
        timeTrackingState.smartTimeSettingsLoaded = true;
    }
}

async function ensureSmartTimeDataForEvents(events) {
    await ensureSmartTimeSettings();
    const locationIds = [...new Set(events.map(e => e.location_id).filter(Boolean))];
    const missingLocationIds = locationIds.filter(id => !timeTrackingState.smartTimeLocationCache.has(id));

    await Promise.all(missingLocationIds.map(async locationId => {
        try {
            const response = await fetch(`${API_BASE_URL}/locations/${locationId}`, {
                headers: { 'Authorization': `Bearer ${auth.getToken()}` }
            });
            if (!response.ok) throw new Error('Failed to load location');
            const location = await response.json();
            const userMap = new Map();
            (location.smart_time_users || []).forEach(entry => {
                userMap.set(entry.user_id, entry.expected_daily_hours);
            });
            timeTrackingState.smartTimeLocationCache.set(locationId, userMap);
        } catch (error) {
            console.error('Failed to load SmartTime users for location:', locationId, error);
            timeTrackingState.smartTimeLocationCache.set(locationId, new Map());
        }
    }));
}

function getSmartTimeSuggestion(event) {
    const locationId = event.location_id;
    const userId = event.user_id;
    if (!locationId || !userId) return null;

    const locationMap = timeTrackingState.smartTimeLocationCache.get(locationId);
    if (!locationMap || !locationMap.has(userId)) return null;

    const expectedHours = locationMap.get(userId);
    if (!expectedHours || expectedHours <= 0) return null;

    let durationMinutes = event.duration_minutes;
    const hasCheckOut = !!event.check_out_time;
    const checkIn = event.check_in_time ? new Date(event.check_in_time) : null;
    const checkOut = event.check_out_time ? new Date(event.check_out_time) : null;
    if (!checkIn) return null;

    if (!hasCheckOut && (durationMinutes === null || durationMinutes === undefined)) {
        const hoursSinceCheckIn = (Date.now() - checkIn.getTime()) / (1000 * 60 * 60);
        if (hoursSinceCheckIn < 30) return null;
        durationMinutes = 0;
    }

    if (durationMinutes === null || durationMinutes === undefined || durationMinutes < 0) return null;

    const expectedMinutes = Math.round(expectedHours * 60);
    const minDeviation = timeTrackingState.smartTimeSettings.minimumDeviationMinutes || 0;
    const randomDeviation = timeTrackingState.smartTimeSettings.randomDeviationMinutes || 0;

    if (expectedMinutes - durationMinutes <= minDeviation) return null;

    const randomMinutes = getDeterministicRandomMinutes(event._id, randomDeviation);
    const suggestedDurationMinutes = expectedMinutes - minDeviation + randomMinutes;

    if (suggestedDurationMinutes <= durationMinutes) return null;

    const formatDuration = (mins) => `${Math.floor(mins / 60)}h ${mins % 60}m`;

    // Suggestion A: keep check-in, push check-out later so duration ≈ expected.
    const suggestedCheckOut = new Date(checkIn.getTime() + suggestedDurationMinutes * 60000);
    const checkOutSuggestion = {
        mode: 'check_out',
        suggestedISO: suggestedCheckOut.toISOString(),
        display: formatDateTime(suggestedCheckOut.toISOString()),
        durationDisplay: formatDuration(suggestedDurationMinutes)
    };

    // Suggestion B: keep check-out (when present), pull check-in earlier so duration ≈ expected.
    let checkInSuggestion = null;
    if (checkOut) {
        const suggestedCheckIn = new Date(checkOut.getTime() - suggestedDurationMinutes * 60000);
        checkInSuggestion = {
            mode: 'check_in',
            suggestedISO: suggestedCheckIn.toISOString(),
            display: formatDateTime(suggestedCheckIn.toISOString()),
            durationDisplay: formatDuration(suggestedDurationMinutes)
        };
    }

    return {
        // Back-compat fields used by the count display
        suggestedCheckOutISO: checkOutSuggestion.suggestedISO,
        checkOutDisplay: checkOutSuggestion.display,
        durationDisplay: checkOutSuggestion.durationDisplay,
        // New: explicit per-mode suggestions
        checkOut: checkOutSuggestion,
        checkIn: checkInSuggestion
    };
}

function getDeterministicRandomMinutes(seed, range) {
    if (!range || range <= 0) return 0;
    let hash = 0;
    const input = String(seed);
    for (let i = 0; i < input.length; i++) {
        hash = (hash * 31 + input.charCodeAt(i)) % 2147483647;
    }
    return Math.abs(hash) % (range + 1);
}

function toggleSmartTimeRow(eventId) {
    if (timeTrackingState.smartTimeOpenEvents.has(eventId)) {
        timeTrackingState.smartTimeOpenEvents.delete(eventId);
    } else {
        timeTrackingState.smartTimeOpenEvents.add(eventId);
    }
    displayTimeTrackingEvents(timeTrackingState.allEvents);
}

async function acceptSmartTimeSuggestion(eventId, mode = 'check_out') {
    const event = timeTrackingState.allEvents.find(e => e._id === eventId);
    if (!event) {
        showError('Event not found');
        return;
    }
    const suggestion = getSmartTimeSuggestion(event);
    if (!suggestion) {
        showError('SmartTime suggestion not available');
        return;
    }

    const chosen = mode === 'check_in' ? suggestion.checkIn : suggestion.checkOut;
    if (!chosen) {
        showError('SmartTime suggestion not available');
        return;
    }

    try {
        const body = {
            reason: 'SmartTime suggestion'
        };
        if (chosen.mode === 'check_in') {
            body.check_in_time = chosen.suggestedISO;
        } else {
            body.check_out_time = chosen.suggestedISO;
        }
        const response = await fetch(`${API_BASE_URL}/time-tracking/events/${eventId}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${auth.getToken()}`
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to apply SmartTime');
        }

        showSuccess('SmartTime applied successfully');
        timeTrackingState.smartTimeOpenEvents.delete(eventId);
        await loadTimeTrackingData(true);
    } catch (error) {
        console.error('Error applying SmartTime:', error);
        showError(error.message || 'Failed to apply SmartTime');
    }
}

/**
 * Filter time tracking (debounced for search)
 */
let searchDebounceTimer = null;
export function debouncedFilterTimeTracking() {
    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }
    
    searchDebounceTimer = setTimeout(() => {
        const userSearch = document.getElementById('time-tracking-user-search')?.value || '';
        // Only filter once at least 2 characters are entered, or if cleared
        if (userSearch.length === 0 || userSearch.length >= 2) {
            filterTimeTracking();
        }
    }, 300);
}

/**
 * Apply filters
 */
export function filterTimeTracking() {
    const dateFrom = document.getElementById('time-tracking-date-from')?.value || '';
    const dateTo = document.getElementById('time-tracking-date-to')?.value || '';
    const status = document.getElementById('time-tracking-status-filter')?.value || '';
    
    timeTrackingState.filters.dateFrom = dateFrom;
    timeTrackingState.filters.dateTo = dateTo;
    timeTrackingState.filters.status = status;
    
    loadTimeTrackingData(true);
}

/**
 * Clear all filters
 */
export function clearTimeTrackingFilters() {
    const dateFromInput = document.getElementById('time-tracking-date-from');
    const dateToInput = document.getElementById('time-tracking-date-to');
    const statusFilter = document.getElementById('time-tracking-status-filter');
    const userSearch = document.getElementById('time-tracking-user-search');
    
    // Reset to default date range (1 month)
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    
    if (dateFromInput) dateFromInput.value = formatLocalDate(oneMonthAgo);
    if (dateToInput) dateToInput.value = formatLocalDate(today);
    if (statusFilter) statusFilter.value = '';
    if (userSearch) userSearch.value = '';
    
    // Clear selected users
    selectedTimeTrackingUsers.clear();
    renderTimeTrackingUserChips();
    const userResults = document.getElementById('time-tracking-user-results');
    if (userResults) {
        userResults.classList.add('hidden');
        userResults.innerHTML = '';
    }
    
    // Clear account filter
    selectedTimeTrackingAccounts.clear();
    const accountDisplay = document.getElementById('time-tracking-account-selected');
    if (accountDisplay) {
        accountDisplay.textContent = t('time.allAccounts');
    }
    // Uncheck all account checkboxes
    const accountCheckboxes = document.querySelectorAll('#time-tracking-account-tree input[type="checkbox"]');
    accountCheckboxes.forEach(cb => cb.checked = false);
    
    // Clear location filter
    selectedTimeTrackingLocations.clear();
    const locationDisplay = document.getElementById('time-tracking-location-selected');
    if (locationDisplay) {
        locationDisplay.textContent = t('time.allLocations');
    }
    // Reset unknown location filter
    filterUnknownLocation = false;
    const unknownLocationCb = document.getElementById('time-tracking-unknown-location-checkbox');
    if (unknownLocationCb) unknownLocationCb.checked = false;
    // Reset unknown user filter
    filterUnknownUser = false;
    const unknownUserCb = document.getElementById('time-tracking-unknown-user-checkbox');
    if (unknownUserCb) unknownUserCb.checked = false;
    // Reload locations without account filter
    loadTimeTrackingLocations(true);
    
    filterTimeTracking();
}

/**
 * Setup user search for template selection
 */
function setupTemplateUserSearch() {
    const searchInput = document.getElementById('template-user-search');
    const resultsDiv = document.getElementById('template-user-results');
    const hiddenInput = document.getElementById('template-user-id');
    let searchTimeout;
    
    searchInput.addEventListener('input', (e) => {
        const query = e.target.value.trim();
        clearTimeout(searchTimeout);
        
        // Clear selection when user types
        hiddenInput.value = '';
        updateTemplatePreview();
        
        if (query.length < 2) {
            resultsDiv.classList.add('hidden');
            return;
        }
        
        searchTimeout = setTimeout(async () => {
            try {
                // Get effective account ID for filtering
                const accountId = getEffectiveAccountId();
                if (!accountId) {
                    resultsDiv.innerHTML = '<div class="p-2 text-gray-500">No account selected</div>';
                    resultsDiv.classList.remove('hidden');
                    return;
                }
                
                // Build URL with account filtering
                const params = new URLSearchParams();
                params.append('search', query);
                params.append('limit', '20');
                params.append('account_id', accountId);
                params.append('include_child_accounts', 'true');
                
                const response = await fetch(`/api/users/?${params.toString()}`, {
                    headers: auth.getAuthHeaders()
                });
                
                if (!response.ok) throw new Error('Search failed');
                
                const data = await response.json();
                const users = data.users || [];
                
                if (users.length === 0) {
                    resultsDiv.innerHTML = '<div class="p-2 text-gray-500">No users found</div>';
                } else {
                    resultsDiv.innerHTML = users.map(user => `
                        <div class="p-2 hover:bg-gray-100 cursor-pointer" onclick="selectTemplateUser('${user.id || user._id}', '${(user.full_name || user.username).replace(/'/g, "\\'")}')">
                            ${user.full_name || user.username}${user.email ? ` (${user.email})` : ''}
                        </div>
                    `).join('');
                }
                resultsDiv.classList.remove('hidden');
            } catch (error) {
                console.error('User search error:', error);
            }
        }, 300);
    });
    
    // Hide results when clicking outside
    document.addEventListener('click', (e) => {
        if (!searchInput.contains(e.target) && !resultsDiv.contains(e.target)) {
            resultsDiv.classList.add('hidden');
        }
    });
}

/**
 * Setup location search for template selection
 */
function setupTemplateLocationSearch() {
    const searchInput = document.getElementById('template-location-search');
    const resultsDiv = document.getElementById('template-location-results');
    const hiddenInput = document.getElementById('template-location-id');
    let searchTimeout;
    
    searchInput.addEventListener('input', (e) => {
        const query = e.target.value.trim();
        clearTimeout(searchTimeout);
        
        // Clear selection when user types
        hiddenInput.value = '';
        updateTemplatePreview();
        
        if (query.length < 2) {
            resultsDiv.classList.add('hidden');
            return;
        }
        
        searchTimeout = setTimeout(async () => {
            try {
                // Build URL with search only - backend will apply account filtering automatically
                const params = new URLSearchParams();
                params.append('search', query);
                params.append('limit', '20');
                
                const response = await fetch(`/api/locations/?${params.toString()}`, {
                    headers: auth.getAuthHeaders()
                });
                
                if (!response.ok) throw new Error('Search failed');
                
                const data = await response.json();
                const locations = data.locations || [];
                
                if (locations.length === 0) {
                    resultsDiv.innerHTML = '<div class="p-2 text-gray-500">No locations found</div>';
                } else {
                    resultsDiv.innerHTML = locations.map(location => `
                        <div class="p-2 hover:bg-gray-100 cursor-pointer" onclick="selectTemplateLocation('${location.id || location._id}', '${(location.name).replace(/'/g, "\\'")}')">
                            ${location.name}${location.address ? ` - ${location.address}` : ''}
                        </div>
                    `).join('');
                }
                resultsDiv.classList.remove('hidden');
            } catch (error) {
                console.error('Location search error:', error);
            }
        }, 300);
    });
    
    // Hide results when clicking outside
    document.addEventListener('click', (e) => {
        if (!searchInput.contains(e.target) && !resultsDiv.contains(e.target)) {
            resultsDiv.classList.add('hidden');
        }
    });
}

/**
 * Select a user from template search results
 */
function selectTemplateUser(userId, userName) {
    document.getElementById('template-user-id').value = userId;
    document.getElementById('template-user-search').value = userName;
    document.getElementById('template-user-results').classList.add('hidden');
    updateTemplatePreview();
}

/**
 * Select a location from template search results
 */
function selectTemplateLocation(locationId, locationName) {
    document.getElementById('template-location-id').value = locationId;
    document.getElementById('template-location-search').value = locationName;
    document.getElementById('template-location-results').classList.add('hidden');
    updateTemplatePreview();
}

/**
 * Update template preview text
 */
export function updateTemplatePreview() {
    const userIdInput = document.getElementById('template-user-id');
    const locationIdInput = document.getElementById('template-location-id');
    const userSearch = document.getElementById('template-user-search');
    const locationSearch = document.getElementById('template-location-search');
    const infoDiv = document.getElementById('template-info');
    
    if (!infoDiv) return;
    
    const userId = userIdInput?.value;
    const locationId = locationIdInput?.value;
    
    if (userId && locationId) {
        const userName = userSearch?.value || 'selected user';
        const locationName = locationSearch?.value || 'selected location';
        infoDiv.innerHTML = `<span class="text-green-600">✓ Creating template for <strong>${userName}</strong> at <strong>${locationName}</strong></span>`;
    } else if (userId || locationId) {
        infoDiv.innerHTML = `<span class="text-orange-600">⚠ Please select both user and location, or leave both empty for general template</span>`;
    } else {
        infoDiv.innerHTML = 'Select a user and location to create a simple template for that person, or leave empty for a general template with all users and locations.';
    }
}

/**
 * Open import events modal
 */
export async function openImportEventsModal() {
    const modal = document.createElement('div');
    modal.id = 'import-events-modal';
    modal.className = 'fixed z-50 inset-0 overflow-y-auto';
    modal.innerHTML = `
        <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            
            <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Import Time Tracking Events
                    </h3>
                    
                    <div class="space-y-4">
                        <!-- Instructions -->
                        <div class="bg-blue-50 border-l-4 border-blue-400 p-4">
                            <div class="flex">
                                <div class="flex-shrink-0">
                                    <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                                    </svg>
                                </div>
                                <div class="ml-3">
                                    <p class="text-sm text-blue-700">
                                        Download the template first, fill in your data, then upload the completed file.
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Template Options -->
                        <div class="space-y-3">
                            <label class="block text-sm font-medium text-gray-700">
                                Template Type
                            </label>
                            
                            <!-- User Selector -->
                            <div>
                                <label class="block text-sm text-gray-600 mb-1">
                                    Select User (Optional - for specific user template)
                                </label>
                                <input type="text" id="template-user-search" 
                                       placeholder="Type to search for a user..."
                                       class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                <input type="hidden" id="template-user-id">
                                <div id="template-user-results" class="mt-1 max-h-48 overflow-y-auto bg-white border border-gray-300 rounded-md hidden"></div>
                            </div>
                            
                            <!-- Location Selector -->
                            <div>
                                <label class="block text-sm text-gray-600 mb-1">
                                    Select Location (Optional - for specific location template)
                                </label>
                                <input type="text" id="template-location-search" 
                                       placeholder="Type to search for a location..."
                                       class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                <input type="hidden" id="template-location-id">
                                <div id="template-location-results" class="mt-1 max-h-48 overflow-y-auto bg-white border border-gray-300 rounded-md hidden"></div>
                            </div>
                            
                            <!-- Template Info -->
                            <div id="template-info" class="text-xs text-gray-500 italic">
                                Select a user and location to create a simple template for that person, or leave empty for a general template with all users and locations.
                            </div>
                            
                            <!-- Download Button -->
                            <button onclick="downloadImportTemplate()" 
                                    class="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                                <svg class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                </svg>
                                Download Excel Template
                            </button>
                        </div>
                        
                        <!-- File Upload -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Upload Completed Excel File
                            </label>
                            <div class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                                <div class="space-y-1 text-center">
                                    <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <div class="flex text-sm text-gray-600">
                                        <label for="import-file-input" class="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                                            <span>Upload a file</span>
                                            <input id="import-file-input" type="file" accept=".xlsx,.xls" class="sr-only" onchange="handleImportFileSelect(event)">
                                        </label>
                                        <p class="pl-1">or drag and drop</p>
                                    </div>
                                    <p class="text-xs text-gray-500">Excel files only (.xlsx, .xls)</p>
                                </div>
                            </div>
                            <div id="selected-file-name" class="mt-2 text-sm text-gray-600"></div>
                        </div>
                        
                        <!-- Progress/Results -->
                        <div id="import-progress" class="hidden">
                            <div class="bg-gray-50 rounded-md p-4">
                                <div class="flex items-center justify-center">
                                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                    <span class="ml-3 text-sm text-gray-700">Importing events...</span>
                                </div>
                            </div>
                        </div>
                        
                        <div id="import-results" class="hidden"></div>
                    </div>
                </div>
                
                <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button id="import-upload-button" onclick="uploadImportFile()" disabled
                            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                        Upload & Import
                    </button>
                    <button onclick="closeImportEventsModal()" 
                            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                        Close
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    
    // Setup search handlers
    setupTemplateUserSearch();
    setupTemplateLocationSearch();
    
    // Initialize preview
    updateTemplatePreview();
}

/**
 * Close import events modal
 */
export function closeImportEventsModal() {
    const modal = document.getElementById('import-events-modal');
    if (modal) {
        modal.remove();
    }
}

/**
 * Download import template
 */
export async function downloadImportTemplate() {
    try {
        // Get selected user and location from hidden inputs
        const userIdInput = document.getElementById('template-user-id');
        const locationIdInput = document.getElementById('template-location-id');
        
        const userId = userIdInput?.value || '';
        const locationId = locationIdInput?.value || '';
        
        // Check if both or neither are selected
        if ((userId && !locationId) || (!userId && locationId)) {
            showError('Please select both user and location, or leave both empty for a general template');
            return;
        }
        
        // Build URL with query parameters
        let url = '/api/time-tracking/import-template';
        const params = new URLSearchParams();
        
        if (userId && locationId) {
            params.append('user_id', userId);
            params.append('location_id', locationId);
        }
        
        if (params.toString()) {
            url += '?' + params.toString();
        }
        
        // Use fetch directly for binary download (not apiCall which expects JSON)
        const response = await fetch(url, {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        
        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
            throw new Error(error.detail || 'Failed to download template');
        }
        
        // Create blob from response
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        
        // Set filename based on template type
        if (userId && locationId) {
            const userName = document.getElementById('template-user-search').value;
            const locationName = document.getElementById('template-location-search').value;
            a.download = `time_tracking_template_${userName.replace(/\s+/g, '_')}_${locationName.replace(/\s+/g, '_')}.xlsx`;
        } else {
            a.download = 'time_tracking_import_template.xlsx';
        }
        
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(a);
        
        showSuccess('Template downloaded successfully');
    } catch (error) {
        console.error('Error downloading template:', error);
        showError('Failed to download template: ' + (error.message || 'Unknown error'));
    }
}

/**
 * Handle file selection
 */
export function handleImportFileSelect(event) {
    const file = event.target.files[0];
    if (file) {
        const fileNameDisplay = document.getElementById('selected-file-name');
        const uploadButton = document.getElementById('import-upload-button');
        
        fileNameDisplay.textContent = `Selected: ${file.name}`;
        uploadButton.disabled = false;
        
        // Store file for upload
        window.importFileToUpload = file;
    }
}

/**
 * Upload and import file
 */
export async function uploadImportFile() {
    const file = window.importFileToUpload;
    if (!file) {
        showError('Please select a file first');
        return;
    }
    
    const progressDiv = document.getElementById('import-progress');
    const resultsDiv = document.getElementById('import-results');
    const uploadButton = document.getElementById('import-upload-button');
    
    try {
        // Show progress
        progressDiv.classList.remove('hidden');
        resultsDiv.classList.add('hidden');
        uploadButton.disabled = true;
        
        // Create form data
        const formData = new FormData();
        formData.append('file', file);
        
        // Upload file
        const response = await fetch('/api/time-tracking/import', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            },
            body: formData
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Upload failed');
        }
        
        const result = await response.json();
        
        // Hide progress, show results
        progressDiv.classList.add('hidden');
        resultsDiv.classList.remove('hidden');
        
        // Display results
        let resultsHTML = `
            <div class="bg-white rounded-md border ${result.results.failed === 0 ? 'border-green-200' : 'border-yellow-200'} p-4">
                <h4 class="text-sm font-medium ${result.results.failed === 0 ? 'text-green-800' : 'text-yellow-800'} mb-2">
                    Import Complete
                </h4>
                <div class="text-sm space-y-1">
                    <p><strong>Total rows:</strong> ${result.results.total}</p>
                    <p class="text-green-600"><strong>Successful:</strong> ${result.results.successful}</p>
                    ${result.results.failed > 0 ? `<p class="text-red-600"><strong>Failed:</strong> ${result.results.failed}</p>` : ''}
                </div>
        `;
        
        if (result.results.errors && result.results.errors.length > 0) {
            resultsHTML += `
                <div class="mt-3">
                    <p class="text-sm font-medium text-red-800 mb-1">Errors:</p>
                    <div class="max-h-40 overflow-y-auto bg-red-50 rounded p-2">
                        <ul class="text-xs space-y-1">
                            ${result.results.errors.map(err => `
                                <li><strong>Row ${err.row}:</strong> ${err.error}</li>
                            `).join('')}
                        </ul>
                    </div>
                </div>
            `;
        }
        
        resultsHTML += `</div>`;
        resultsDiv.innerHTML = resultsHTML;
        
        // Show success message
        if (result.results.successful > 0) {
            showSuccess(`Successfully imported ${result.results.successful} events`);
            
            // Reload time tracking data after a short delay
            setTimeout(() => {
                loadTimeTrackingData(true);
            }, 1500);
        }
        
    } catch (error) {
        progressDiv.classList.add('hidden');
        resultsDiv.classList.remove('hidden');
        resultsDiv.innerHTML = `
            <div class="bg-red-50 border border-red-200 rounded-md p-4">
                <p class="text-sm text-red-800">
                    <strong>Error:</strong> ${error.message || 'Unknown error occurred'}
                </p>
            </div>
        `;
        showError('Import failed: ' + (error.message || 'Unknown error'));
    } finally {
        uploadButton.disabled = false;
    }
}

/*
 * Account and Location Filter Functions
 */

let selectedTimeTrackingAccounts = new Set();
let accountHierarchyMap = {}; // Store account hierarchy for filtering
let selectedTimeTrackingLocations = new Set();
let filterUnknownLocation = false;
let filterUnknownUser = false;
// Map of selected user id -> display name (used by the multi-select user filter)
let selectedTimeTrackingUsers = new Map();
let timeTrackingLocationsState = {
    allLocations: [],
    filteredLocations: [],
    currentSkip: 0,
    limit: 50,
    hasMore: true,
    isLoading: false,
    searchTerm: ''
};

// Build a flat map of all account IDs including children
function buildAccountHierarchyMap(accounts) {
    const map = {};
    
    function processAccount(account) {
        const accountId = account._id || account.id;
        const childIds = [];
        
        if (account.children && account.children.length > 0) {
            account.children.forEach(child => {
                const childId = child._id || child.id;
                childIds.push(childId);
                processAccount(child); // Recursively process children
            });
        }
        
        map[accountId] = {
            id: accountId,
            name: account.account_name || account.name,
            children: childIds
        };
    }
    
    accounts.forEach(account => processAccount(account));
    return map;
}

// Get all account IDs including descendants
function getAllAccountIdsIncludingDescendants(accountId) {
    const result = [accountId];
    
    function addDescendants(id) {
        const account = accountHierarchyMap[id];
        if (account && account.children.length > 0) {
            account.children.forEach(childId => {
                result.push(childId);
                addDescendants(childId);
            });
        }
    }
    
    addDescendants(accountId);
    return result;
}

// Get all ancestor account IDs (parents, grandparents, ...) of a given account
function getAllAncestorAccountIds(accountId) {
    const ancestors = [];
    // Build a child->parent lookup on the fly from the existing map
    const parentOf = {};
    Object.values(accountHierarchyMap).forEach(node => {
        if (node && node.children) {
            node.children.forEach(childId => {
                parentOf[childId] = node.id;
            });
        }
    });
    let current = parentOf[accountId];
    const guard = new Set();
    while (current && !guard.has(current)) {
        guard.add(current);
        ancestors.push(current);
        current = parentOf[current];
    }
    return ancestors;
}

export async function loadAccountsForTimeTracking() {
    try {
        console.log('[TIME TRACKING] Fetching accounts from /api/accounts/hierarchy');
        const response = await fetch(`${API_BASE_URL}/accounts/hierarchy`, {
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });
        
        console.log('[TIME TRACKING] Response status:', response.status);
        
        if (response.ok) {
            const data = await response.json();
            console.log('[TIME TRACKING] Received hierarchy response:', data);
            
            // Extract accounts_tree from response
            const accountsTree = data.accounts_tree || data;

            // Append extra time-tracking accounts granted to this user that are
            // outside their own hierarchy (Team leader / Team viewer feature)
            const currentUser = getCurrentUser();
            const extraAccounts = (currentUser && currentUser.time_tracking_accounts) || [];
            if (extraAccounts.length > 0) {
                const existingIds = new Set(collectAllAccountIds(accountsTree));
                for (const acct of extraAccounts) {
                    if (!existingIds.has(acct.id)) {
                        accountsTree.push({
                            _id: acct.id,
                            account_name: acct.account_name,
                            children: []
                        });
                    }
                }
            }
            
            // Build hierarchy map for filtering
            accountHierarchyMap = buildAccountHierarchyMap(accountsTree);
            console.log('[TIME TRACKING] Account hierarchy map:', accountHierarchyMap);
            
            // Render tree with checkboxes
            const treeContainer = document.getElementById('time-tracking-account-tree');
            if (treeContainer) {
                treeContainer.innerHTML = renderTimeTrackingAccountTree(accountsTree);
            }
        }
    } catch (error) {
        console.error('Error loading accounts:', error);
    }
}

/**
 * Collect all account IDs from a tree (flat list).
 */
function collectAllAccountIds(accounts) {
    const ids = [];
    for (const a of accounts) {
        ids.push(a._id || a.id);
        if (a.children && a.children.length) {
            ids.push(...collectAllAccountIds(a.children));
        }
    }
    return ids;
}

function renderTimeTrackingAccountTree(accounts, level = 0) {
    let html = '';
    
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 20;
        // Auto-expand top level: customers always have sub-accounts holding the
        // actual locations, so saving the first click speeds up the daily flow.
        const expandByDefault = level === 0;

        html += `
            <div class="account-tree-item" data-level="${level}">
                <div class="flex items-center py-1.5 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="toggleTimeTrackingAccountItem('tt-tree-${accountId}')" 
                                class="mr-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
                            <svg id="tt-tree-icon-${accountId}" class="w-4 h-4 transform transition-transform${expandByDefault ? ' rotate-90' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="w-5 mr-1"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="checkbox" value="${accountId}" 
                               onchange="selectTimeTrackingAccount('${accountId}', '${accountName.replace(/'/g, "\\'")}')"
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="tt-tree-${accountId}"${expandByDefault ? '' : ' class="hidden"'}>
                        ${renderTimeTrackingAccountTree(account.children, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    
    return html;
}

export function toggleTimeTrackingAccountItem(itemId) {
    const item = document.getElementById(itemId);
    const iconId = itemId.replace('tt-tree-', 'tt-tree-icon-');
    const icon = document.getElementById(iconId);
    
    if (item && icon) {
        item.classList.toggle('hidden');
        icon.classList.toggle('rotate-90');
    }
}

export function selectTimeTrackingAccount(accountId, accountName) {
    const checkbox = event.target;
    
    if (checkbox.checked) {
        selectedTimeTrackingAccounts.add(accountId);
    } else {
        selectedTimeTrackingAccounts.delete(accountId);
    }
    
    // Update display text
    const displayElement = document.getElementById('time-tracking-account-selected');
    if (displayElement) {
        if (selectedTimeTrackingAccounts.size === 0) {
            displayElement.textContent = t('time.allAccounts');
        } else if (selectedTimeTrackingAccounts.size === 1) {
            displayElement.textContent = accountName;
        } else {
            displayElement.textContent = `${selectedTimeTrackingAccounts.size} accounts selected`;
        }
    }
    
    // Reload locations based on selected accounts
    loadTimeTrackingLocations(true);
    
    // Re-filter the time tracking data
    filterTimeTracking();
}

export function toggleTimeTrackingAccountTree() {
    const container = document.getElementById('time-tracking-account-tree-container');
    if (container) {
        // Close location dropdown when opening account tree
        if (container.classList.contains('hidden')) {
            document.getElementById('time-tracking-location-dropdown')?.classList.add('hidden');
        }
        container.classList.toggle('hidden');
    }
}

export function toggleTimeTrackingLocationDropdown() {
    const dropdown = document.getElementById('time-tracking-location-dropdown');
    if (dropdown) {
        const isHidden = dropdown.classList.contains('hidden');
        // Close account tree when opening location dropdown
        if (isHidden) {
            document.getElementById('time-tracking-account-tree-container')?.classList.add('hidden');
        }
        dropdown.classList.toggle('hidden');
        
        // Load initial locations when opening
        if (isHidden && timeTrackingLocationsState.allLocations.length === 0) {
            loadTimeTrackingLocations(true);
        }
    }
}

export function toggleUnknownLocationFilter() {
    const cb = document.getElementById('time-tracking-unknown-location-checkbox');
    filterUnknownLocation = cb ? cb.checked : !filterUnknownLocation;

    const locationDisplay = document.getElementById('time-tracking-location-selected');
    if (locationDisplay) {
        if (filterUnknownLocation) {
            locationDisplay.textContent = 'Unknown location';
        } else {
            const count = selectedTimeTrackingLocations.size;
            locationDisplay.textContent = count > 0
                ? `${count} location${count > 1 ? 's' : ''} selected`
                : t('time.allLocations');
        }
    }
    filterTimeTracking();
}

/**
 * Toggle the "Show only Unknown Users" filter. Unknown User events are
 * scans of NFC cards that have not yet been associated with a user — admins
 * use this to find them quickly so the cards can be registered.
 */
export function toggleUnknownUserFilter() {
    const cb = document.getElementById('time-tracking-unknown-user-checkbox');
    filterUnknownUser = cb ? cb.checked : !filterUnknownUser;
    filterTimeTracking();
}

/**
 * Setup the multi-select user search filter for the Time Tracking event list.
 * Typing in the input fetches matching users and displays them in a dropdown.
 * Clicking a user adds them as a chip; chips can be removed via the × button.
 * Filtering is applied whenever the selected-user set changes.
 */
function setupTimeTrackingUserSearch() {
    const searchInput = document.getElementById('time-tracking-user-search');
    const resultsDiv = document.getElementById('time-tracking-user-results');
    if (!searchInput || !resultsDiv) return;

    let searchTimeout;
    let lastQuery = '';

    const runSearch = async (query) => {
        try {
            const params = new URLSearchParams();
            params.append('search', query);
            params.append('limit', '20');
            params.append('include_child_accounts', 'true');
            // Time tracking events at a sub-account location are often performed
            // by users that live on a parent (top-level) account. Make sure the
            // user search isn't artificially limited to the selected sub-account
            // hierarchy and includes parent accounts as well.
            params.append('include_parent_accounts', 'true');

            // Scope to selected accounts when present, otherwise the user's effective account
            if (selectedTimeTrackingAccounts.size > 0) {
                const accountIds = new Set();
                selectedTimeTrackingAccounts.forEach(accountId => {
                    // Selected account + all descendants
                    getAllAccountIdsIncludingDescendants(accountId).forEach(id => accountIds.add(id));
                    // …and all ancestors, so users on top accounts are findable
                    getAllAncestorAccountIds(accountId).forEach(id => accountIds.add(id));
                });
                accountIds.forEach(id => params.append('account_id', id));
            } else {
                const effectiveAccountId = getEffectiveAccountId();
                if (effectiveAccountId) {
                    params.append('account_id', effectiveAccountId);
                }
            }

            const response = await fetch(`${API_BASE_URL}/users/?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${auth.getToken()}` }
            });
            if (!response.ok) throw new Error('User search failed');
            const data = await response.json();
            const users = data.users || [];
            renderUserSearchResults(users);
        } catch (error) {
            console.error('User search error:', error);
            resultsDiv.innerHTML = `<div class="px-3 py-2 text-sm text-gray-500">${t('common.error') || 'Error'}</div>`;
            resultsDiv.classList.remove('hidden');
        }
    };

    const renderUserSearchResults = (users) => {
        // Filter out users that are already selected
        const remaining = users.filter(u => !selectedTimeTrackingUsers.has(u.id || u._id));
        if (remaining.length === 0) {
            resultsDiv.innerHTML = `<div class="px-3 py-2 text-sm text-gray-500">${t('common.noResults') || 'No users found'}</div>`;
        } else {
            resultsDiv.innerHTML = remaining.map(user => {
                const id = user.id || user._id;
                const name = user.full_name || user.username || user.email || 'Unknown';
                const safeName = name.replace(/'/g, "\\'");
                return `
                    <div class="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-700"
                         onclick="event.stopPropagation(); selectTimeTrackingUser('${id}', '${safeName}')">
                        ${name}
                    </div>
                `;
            }).join('');
        }
        resultsDiv.classList.remove('hidden');
    };

    searchInput.addEventListener('input', () => {
        const query = searchInput.value.trim();
        clearTimeout(searchTimeout);

        if (query.length < 2) {
            resultsDiv.classList.add('hidden');
            resultsDiv.innerHTML = '';
            lastQuery = '';
            return;
        }
        if (query === lastQuery) return;

        searchTimeout = setTimeout(() => {
            lastQuery = query;
            runSearch(query);
        }, 300);
    });

    searchInput.addEventListener('focus', () => {
        if (searchInput.value.trim().length >= 2 && resultsDiv.innerHTML) {
            resultsDiv.classList.remove('hidden');
        }
    });

    // Backspace on empty input removes the last selected user
    searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Backspace' && searchInput.value === '' && selectedTimeTrackingUsers.size > 0) {
            const lastId = Array.from(selectedTimeTrackingUsers.keys()).pop();
            removeTimeTrackingUser(lastId);
        }
    });

    renderTimeTrackingUserChips();
}

/**
 * Render the chips representing currently selected users.
 */
function renderTimeTrackingUserChips() {
    const chipsContainer = document.getElementById('time-tracking-user-chips');
    if (!chipsContainer) return;

    if (selectedTimeTrackingUsers.size === 0) {
        chipsContainer.innerHTML = '';
        return;
    }

    const chips = [];
    selectedTimeTrackingUsers.forEach((name, id) => {
        const safeName = (name || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        chips.push(`
            <span class="inline-flex items-center gap-1 rounded-full bg-primary-100 text-primary-800 text-xs font-medium px-2 py-1">
                ${safeName}
                <button type="button"
                        onclick="removeTimeTrackingUser('${id}')"
                        class="ml-0.5 inline-flex items-center justify-center rounded-full hover:bg-primary-200 focus:outline-none"
                        aria-label="Remove ${safeName}">
                    <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            </span>
        `);
    });
    chipsContainer.innerHTML = chips.join('');
}

/**
 * Add a user to the selected-users set and trigger a re-filter.
 * Keeps the search input and dropdown open so the user can keep picking
 * matches from the same result list (useful for generic names like
 * "Substitute1", "Substitute2", ...). Click-outside hides the dropdown.
 */
export function selectTimeTrackingUser(userId, userName) {
    if (!userId || selectedTimeTrackingUsers.has(userId)) return;
    selectedTimeTrackingUsers.set(userId, userName);

    renderTimeTrackingUserChips();

    // Re-render the dropdown so the just-selected user disappears from the list
    const resultsDiv = document.getElementById('time-tracking-user-results');
    if (resultsDiv) {
        const remaining = resultsDiv.querySelectorAll('div[onclick]');
        let visibleCount = 0;
        remaining.forEach(div => {
            if (div.getAttribute('onclick').includes(`'${userId}'`)) {
                div.remove();
            } else {
                visibleCount++;
            }
        });
        if (visibleCount === 0) {
            resultsDiv.innerHTML = `<div class="px-3 py-2 text-sm text-gray-500">${t('common.noResults') || 'No users found'}</div>`;
        }
        resultsDiv.classList.remove('hidden');
    }

    // Keep focus on the input so the user can continue searching/selecting
    const searchInput = document.getElementById('time-tracking-user-search');
    if (searchInput) searchInput.focus();

    filterTimeTracking();
}

/**
 * Remove a user from the selected-users set and trigger a re-filter.
 */
export function removeTimeTrackingUser(userId) {
    if (!selectedTimeTrackingUsers.has(userId)) return;
    selectedTimeTrackingUsers.delete(userId);
    renderTimeTrackingUserChips();
    filterTimeTracking();
}

function setupTimeTrackingLocationSearch() {
    const searchInput = document.getElementById('time-tracking-location-search');
    const locationList = document.getElementById('time-tracking-location-list');
    let searchTimeout;
    
    if (!searchInput || !locationList) return;
    
    // Handle search input
    searchInput.addEventListener('input', function() {
        const searchTerm = this.value.trim();
        
        clearTimeout(searchTimeout);
        
        searchTimeout = setTimeout(() => {
            timeTrackingLocationsState.searchTerm = searchTerm;
            loadTimeTrackingLocations(true);
        }, 300);
    });
    
    // Setup infinite scroll
    locationList.addEventListener('scroll', function() {
        // Trigger loading when user scrolls within 100px of the bottom
        if (this.scrollTop + this.clientHeight >= this.scrollHeight - 100) {
            loadTimeTrackingLocations(false);
        }
    });
}

async function loadTimeTrackingLocations(reset = false) {
    if (reset) {
        timeTrackingLocationsState.currentSkip = 0;
        timeTrackingLocationsState.allLocations = [];
        timeTrackingLocationsState.hasMore = true;
    }
    
    if (timeTrackingLocationsState.isLoading || !timeTrackingLocationsState.hasMore) {
        return;
    }
    
    timeTrackingLocationsState.isLoading = true;
    const startTime = performance.now();
    
    try {
        // Build query parameters including account filter
        const params = new URLSearchParams();
        params.append('skip', timeTrackingLocationsState.currentSkip);
        params.append('limit', timeTrackingLocationsState.limit);
        params.append('minimal', 'true');  // Request minimal data for faster loading
        
        if (timeTrackingLocationsState.searchTerm) {
            params.append('search', timeTrackingLocationsState.searchTerm);
        }
        
        // Add account filter if any accounts are selected
        if (selectedTimeTrackingAccounts.size > 0) {
            const accountIds = [];
            selectedTimeTrackingAccounts.forEach(accountId => {
                const idsWithDescendants = getAllAccountIdsIncludingDescendants(accountId);
                accountIds.push(...idsWithDescendants);
            });
            // Remove duplicates
            const uniqueAccountIds = [...new Set(accountIds)];
            uniqueAccountIds.forEach(id => params.append('account_id', id));
        }
        
        const fetchStart = performance.now();
        const response = await fetch(`${API_BASE_URL}/locations?${params.toString()}`, {
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });
        const fetchEnd = performance.now();
        console.log(`[LOCATION LOAD] API call took ${(fetchEnd - fetchStart).toFixed(0)}ms`);
        
        if (!response.ok) throw new Error('Failed to load locations');
        
        const data = await response.json();
        const locations = Array.isArray(data) ? data : data.locations || [];
        
        timeTrackingLocationsState.allLocations = [...timeTrackingLocationsState.allLocations, ...locations]
            .sort((a, b) => (a?.name || '').localeCompare((b?.name || ''), undefined, { sensitivity: 'base' }));
        timeTrackingLocationsState.hasMore = locations.length === timeTrackingLocationsState.limit;
        timeTrackingLocationsState.currentSkip += locations.length;
        
        displayTimeTrackingLocations();
        
        const endTime = performance.now();
        console.log(`[LOCATION LOAD] Total load time: ${(endTime - startTime).toFixed(0)}ms`);
    } catch (error) {
        console.error('Error loading locations:', error);
    } finally {
        timeTrackingLocationsState.isLoading = false;
    }
}

function displayTimeTrackingLocations() {
    const locationList = document.getElementById('time-tracking-location-list');
    if (!locationList) return;
    
    if (timeTrackingLocationsState.allLocations.length === 0) {
        locationList.innerHTML = '<div class="px-3 py-2 text-sm text-gray-500">No locations found</div>';
        return;
    }
    
    let html = '';
    timeTrackingLocationsState.allLocations.forEach(location => {
        const locationId = location._id || location.id;
        const isChecked = selectedTimeTrackingLocations.has(locationId);
        
        html += `
            <div class="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer" onclick="selectTimeTrackingLocation('${locationId}', '${location.name.replace(/'/g, "\\'")}')">
                <input type="checkbox" 
                       ${isChecked ? 'checked' : ''} 
                       class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                       style="pointer-events: none;">
                <label class="ml-2 text-sm text-gray-700 flex-1 cursor-pointer">
                    ${location.name}
                </label>
            </div>
        `;
    });
    
    if (timeTrackingLocationsState.hasMore) {
        html += '<div class="px-3 py-2 text-sm text-gray-500 text-center">Scroll for more...</div>';
    }
    
    locationList.innerHTML = html;
}

export function selectTimeTrackingLocation(locationId, locationName) {
    const checkbox = event.target.closest('div').querySelector('input[type="checkbox"]');
    
    if (selectedTimeTrackingLocations.has(locationId)) {
        selectedTimeTrackingLocations.delete(locationId);
        checkbox.checked = false;
    } else {
        selectedTimeTrackingLocations.add(locationId);
        checkbox.checked = true;
    }
    
    console.log('[LOCATION FILTER] Selected locations:', Array.from(selectedTimeTrackingLocations));
    
    // Update display text
    const displayElement = document.getElementById('time-tracking-location-selected');
    if (displayElement) {
        if (selectedTimeTrackingLocations.size === 0) {
            displayElement.textContent = t('time.allLocations');
        } else if (selectedTimeTrackingLocations.size === 1) {
            displayElement.textContent = locationName;
        } else {
            displayElement.textContent = `${selectedTimeTrackingLocations.size} locations selected`;
        }
    }
    
    // Re-filter the time tracking data
    filterTimeTracking();
}

/**
 * Toggle between viewing current and original times
 */
export function toggleTimeView(mode) {
    timeTrackingState.viewMode = mode;
    
    // Update button states
    const currentBtn = document.getElementById('view-current-btn');
    const originalBtn = document.getElementById('view-original-btn');
    
    if (mode === 'current') {
        if (currentBtn) {
            currentBtn.className = 'relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-primary-600 text-sm font-medium text-white hover:bg-primary-700 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500';
        }
        if (originalBtn) {
            originalBtn.className = 'relative -ml-px inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500';
        }
    } else {
        if (currentBtn) {
            currentBtn.className = 'relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500';
        }
        if (originalBtn) {
            originalBtn.className = 'relative -ml-px inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-primary-600 text-sm font-medium text-white hover:bg-primary-700 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500';
        }
    }
    
    // Refresh display with current data
    displayTimeTrackingEvents(timeTrackingState.allEvents);
}

// Make functions globally accessible
if (typeof window !== 'undefined') {
    window.loadTimeTrackingData = loadTimeTrackingData;
    window.filterTimeTracking = filterTimeTracking;
    window.debouncedFilterTimeTracking = debouncedFilterTimeTracking;
    window.clearTimeTrackingFilters = clearTimeTrackingFilters;
    window.openImportEventsModal = openImportEventsModal;
    window.toggleSmartTimeSuggestions = toggleSmartTimeSuggestions;
    window.toggleSmartTimeRow = toggleSmartTimeRow;
    window.acceptSmartTimeSuggestion = acceptSmartTimeSuggestion;
    window.closeImportEventsModal = closeImportEventsModal;
    window.downloadImportTemplate = downloadImportTemplate;
    window.handleImportFileSelect = handleImportFileSelect;
    window.uploadImportFile = uploadImportFile;
    window.updateTemplatePreview = updateTemplatePreview;
    window.selectTemplateUser = selectTemplateUser;
    window.selectTemplateLocation = selectTemplateLocation;
    window.loadAccountsForTimeTracking = loadAccountsForTimeTracking;
    window.toggleTimeTrackingAccountTree = toggleTimeTrackingAccountTree;
    window.toggleTimeTrackingAccountItem = toggleTimeTrackingAccountItem;
    window.selectTimeTrackingAccount = selectTimeTrackingAccount;
    window.toggleTimeTrackingLocationDropdown = toggleTimeTrackingLocationDropdown;
    window.toggleUnknownLocationFilter = toggleUnknownLocationFilter;
    window.toggleUnknownUserFilter = toggleUnknownUserFilter;
    window.selectTimeTrackingLocation = selectTimeTrackingLocation;
    window.selectTimeTrackingUser = selectTimeTrackingUser;
    window.removeTimeTrackingUser = removeTimeTrackingUser;
    window.toggleTimeView = toggleTimeView;
}

// ============================================
// EXCEL EXPORT FUNCTIONS
// ============================================

/**
 * Sort an array of time tracking events by a chosen sort key (client-side).
 * Returns a new array; original is not mutated. Falls back to date desc.
 *
 * Sort options (now two independent axes):
 *  - dateDir : 'desc' (newest first, default) | 'asc' (oldest first)
 *  - group   : 'none' (default) | 'location' | 'user' |
 *              'location_user' (location, then user) |
 *              'user_location' (user, then location)
 *
 * Group is applied first (so all rows for a location/user are contiguous),
 * then dateDir is the tiebreaker within each group.
 *
 * Accepts either an options object `{ dateDir, group }` or, for backward
 * compatibility, a legacy string key (date_desc / date_asc / location / user /
 * location_user / user_location).
 */
function sortTimeTrackingEvents(events, sortBy) {
    if (!Array.isArray(events) || events.length === 0) return events || [];

    // Normalize legacy string keys to { dateDir, group }
    let dateDir = 'desc';
    let group = 'none';
    if (typeof sortBy === 'string') {
        switch (sortBy) {
            case 'date_asc':       dateDir = 'asc';  group = 'none'; break;
            case 'location':       group = 'location'; break;
            case 'user':           group = 'user'; break;
            case 'location_user':  group = 'location_user'; break;
            case 'user_location':  group = 'user_location'; break;
            case 'date_desc':
            default:               dateDir = 'desc'; group = 'none'; break;
        }
    } else if (sortBy && typeof sortBy === 'object') {
        if (sortBy.dateDir === 'asc' || sortBy.dateDir === 'desc') dateDir = sortBy.dateDir;
        if (typeof sortBy.group === 'string') group = sortBy.group;
    }

    const list = events.slice();
    const cmpStr = (a, b) => (a || '').localeCompare(b || '', 'da', { sensitivity: 'base' });
    const cmpDate = dateDir === 'asc'
        ? (a, b) => new Date(a.check_in_time || 0) - new Date(b.check_in_time || 0)
        : (a, b) => new Date(b.check_in_time || 0) - new Date(a.check_in_time || 0);

    let cmp;
    switch (group) {
        case 'location':
            cmp = (a, b) => cmpStr(a.location_name, b.location_name) || cmpDate(a, b);
            break;
        case 'user':
            cmp = (a, b) => cmpStr(a.user_name, b.user_name) || cmpDate(a, b);
            break;
        case 'location_user':
            cmp = (a, b) =>
                cmpStr(a.location_name, b.location_name) ||
                cmpStr(a.user_name, b.user_name) ||
                cmpDate(a, b);
            break;
        case 'user_location':
            cmp = (a, b) =>
                cmpStr(a.user_name, b.user_name) ||
                cmpStr(a.location_name, b.location_name) ||
                cmpDate(a, b);
            break;
        case 'none':
        default:
            cmp = cmpDate;
            break;
    }

    list.sort(cmp);
    return list;
}

/**
 * HTML snippet for two side-by-side sort dropdowns used in export/PDF modals:
 *  - <prefix>-date  : date direction (newest/oldest first)
 *  - <prefix>-group : grouping by location/user
 */
function renderSortControlsHtml(prefix, label) {
    return `
        <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">${label || 'Sort by'}</label>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <select id="${prefix}-date" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm py-2 px-2">
                    <option value="desc">Date (newest first)</option>
                    <option value="asc">Date (oldest first)</option>
                </select>
                <select id="${prefix}-group" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm py-2 px-2">
                    <option value="none">No grouping</option>
                    <option value="location">Group by Location</option>
                    <option value="user">Group by User</option>
                    <option value="location_user">Group by Location, then User</option>
                    <option value="user_location">Group by User, then Location</option>
                </select>
            </div>
        </div>
    `;
}

/**
 * Read the date + group selects rendered by renderSortControlsHtml.
 * Returns `{ dateDir, group }` with safe defaults.
 */
function readSortControls(prefix, root) {
    const scope = root || document;
    const dateEl = scope.querySelector(`#${prefix}-date`);
    const groupEl = scope.querySelector(`#${prefix}-group`);
    return {
        dateDir: dateEl && (dateEl.value === 'asc' || dateEl.value === 'desc') ? dateEl.value : 'desc',
        group: groupEl && groupEl.value ? groupEl.value : 'none',
    };
}

function exportToExcel(data, filename) {
    if (data.length === 0) {
        showError('No data to export');
        return;
    }
    
    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Convert data array to worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Auto-size columns
    const colWidths = [];
    const headers = Object.keys(data[0]);
    
    headers.forEach((header, i) => {
        const maxLength = Math.max(
            header.length,
            ...data.map(row => String(row[header] || '').length)
        );
        colWidths[i] = { wch: Math.min(maxLength + 2, 50) }; // Cap at 50 characters
    });
    
    ws['!cols'] = colWidths;
    
    // Apply time formatting to time columns so Excel can sum them properly
    const timeColumns = ['Total Time (Hours)', 'Accumulated Hours'];
    
    timeColumns.forEach(timeColName => {
        const timeColumnIndex = headers.indexOf(timeColName);
        if (timeColumnIndex !== -1) {
            const colLetter = XLSX.utils.encode_col(timeColumnIndex);
            
            // Apply [h]:mm format to all cells in this column (allows hours > 24)
            for (let row = 1; row <= data.length; row++) {
                const cellRef = colLetter + (row + 1); // +1 because row 1 is header
                if (ws[cellRef] && typeof ws[cellRef].v === 'number' && ws[cellRef].v > 0) {
                    ws[cellRef].z = '[h]:mm'; // Format code for hours:minutes (allows > 24 hours)
                    ws[cellRef].t = 'n'; // Ensure it's treated as a number
                }
            }
        }
    });
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Export');
    
    // Write workbook and trigger download
    XLSX.writeFile(wb, filename);
}

function showExportModal(entityName, columns, exportFunction, extraOptionsHtml) {
    const modal = document.getElementById('modal');
    if (!modal) return;
    
    modal.innerHTML = `
        <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" id="export-modal-backdrop">
            <div class="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
                <div class="flex justify-between items-center mb-4">
                    <h3 class="text-lg font-medium text-gray-900">Export ${entityName} to Excel</h3>
                    <button onclick="closeModal()" class="text-gray-400 hover:text-gray-500">
                        <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                ${extraOptionsHtml || ''}

                <div class="mb-4">
                    <p class="text-sm text-gray-600 mb-3">Select columns to export:</p>
                    <div class="space-y-2 max-h-96 overflow-y-auto">
                        ${columns.map(col => `
                            <label class="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input type="checkbox" 
                                       class="export-column-checkbox h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" 
                                       value="${col.id}" 
                                       ${col.checked ? 'checked' : ''}>
                                <span class="text-sm text-gray-700">${col.label}</span>
                            </label>
                        `).join('')}
                    </div>
                </div>
                
                <div class="flex justify-end space-x-3">
                    <button onclick="closeModal()" 
                            class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                        Cancel
                    </button>
                    <button onclick="window.currentExportFunction()" 
                            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
                        <svg class="inline-block w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export
                    </button>
                </div>
            </div>
        </div>
    `;
    
    modal.classList.remove('hidden');
    window.currentExportFunction = exportFunction;
}

function showTimeTrackingExportModal() {
    // Base columns that everyone can see
    const baseColumns = [
        { id: 'location_name', label: 'Location', checked: true },
        { id: 'user_name', label: 'User Name', checked: true },
        { id: 'check_in_time', label: 'Check In Time', checked: true },
        { id: 'check_out_time', label: 'Check Out Time', checked: true },
        { id: 'duration', label: 'Total Time (Hours:Minutes:Seconds)', checked: true },
        { id: 'status', label: 'Status', checked: false }
    ];
    
    // Additional columns only visible to Administrator and Team leader
    const adminColumns = [
        { id: 'account_name', label: 'Account', checked: false },
        { id: 'is_modified', label: 'Modified', checked: false },
        { id: 'is_manual', label: 'Manual Entry', checked: false },
        { id: 'created_at', label: 'Created Date', checked: false },
        { id: 'modified_by', label: 'Last Modified By', checked: false },
        { id: 'modified_at', label: 'Last Modified Date', checked: false }
    ];
    
    // Determine which columns to show based on permissions
    let columns = [...baseColumns];
    
    // Only show admin columns if user has permission to view modified badge
    // This matches what they can see on the screen (External users don't see these badges)
    if (permissions.can('timeTracking', 'viewModifiedBadge')) {
        columns = [...columns, ...adminColumns];
    }
    
    showExportModal('Time Tracking', columns, exportTimeTrackingToExcel, renderSortControlsHtml('export-sort', 'Sort by'));
}

function exportTimeTrackingToExcel() {
    const selectedColumns = Array.from(document.querySelectorAll('.export-column-checkbox:checked')).map(cb => cb.value);
    
    if (selectedColumns.length === 0) {
        showError('Please select at least one column to export');
        return;
    }

    // Read sort selection from the export modal (defaults to date desc / no grouping)
    const sortBy = readSortControls('export-sort');

    // Show loading state on the export button
    const exportBtn = document.querySelector('#modal button[onclick="window.currentExportFunction()"]');
    const originalBtnHTML = exportBtn ? exportBtn.innerHTML : null;
    if (exportBtn) {
        exportBtn.disabled = true;
        exportBtn.innerHTML = `<svg class="inline-block animate-spin w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 12a8 8 0 018-8v8z"/></svg> Exporting…`;
    }

    // Build filter params matching the active filters, sent server-side to fetch all matching records
    const params = new URLSearchParams();

    if (timeTrackingState.filters.dateFrom) {
        const startDate = new Date(timeTrackingState.filters.dateFrom);
        startDate.setHours(0, 0, 0, 0);
        params.append('start_date', startDate.toISOString());
    }
    if (timeTrackingState.filters.dateTo) {
        const endDate = new Date(timeTrackingState.filters.dateTo);
        endDate.setHours(23, 59, 59, 999);
        params.append('end_date', endDate.toISOString());
    }
    if (timeTrackingState.filters.status) {
        params.append('status', timeTrackingState.filters.status);
    }
    if (selectedTimeTrackingUsers.size > 0) {
        selectedTimeTrackingUsers.forEach((_name, userId) => {
            params.append('user_id', userId);
        });
    }

    // Expand selected accounts to include descendants and pass to server
    if (selectedTimeTrackingAccounts.size > 0) {
        const accountIdsToInclude = new Set();
        selectedTimeTrackingAccounts.forEach(accountId => {
            getAllAccountIdsIncludingDescendants(accountId).forEach(id => accountIdsToInclude.add(id));
        });
        accountIdsToInclude.forEach(id => params.append('account_id', id));
    }

    // Pass selected locations to server
    selectedTimeTrackingLocations.forEach(id => params.append('location_id', id));

    // Unknown location filter
    if (filterUnknownLocation) {
        params.append('unknown_location', 'true');
    }

    // Unknown user filter
    if (filterUnknownUser) {
        params.append('unknown_user', 'true');
    }

    fetch(`${API_BASE_URL}/time-tracking/events/export?${params}`, {
        headers: { 'Authorization': `Bearer ${auth.getToken()}` }
    })
    .then(async response => {
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.detail || 'Export failed');
        }
        return response.json();
    })
    .then(data => {
        const events = sortTimeTrackingEvents(data.events || [], sortBy);

        const rows = events.map(event => {
            const row = {};
            selectedColumns.forEach(col => {
                switch (col) {
                    case 'location_name':
                        row['Location'] = event.location_name || 'Unknown';
                        break;
                    case 'user_name':
                        row['User Name'] = event.user_name || 'Unknown';
                        break;
                    case 'check_in_time':
                        row['Check In Time'] = formatDateTime(event.check_in_time);
                        break;
                    case 'check_out_time':
                        row['Check Out Time'] = formatDateTime(event.check_out_time);
                        break;
                    case 'duration':
                        row['Total Time (Hours)'] = event.duration_minutes ? event.duration_minutes / 1440 : 0;
                        break;
                    case 'status':
                        row['Status'] = event.status === 'closed' ? 'Completed' : 'Open';
                        break;
                    case 'account_name':
                        row['Account'] = event.account_name || 'Unknown';
                        break;
                    case 'is_modified':
                        row['Modified'] = event.is_modified ? 'Yes' : 'No';
                        break;
                    case 'is_manual':
                        row['Manual Entry'] = event.is_manual ? 'Yes' : 'No';
                        break;
                    case 'created_at':
                        row['Created Date'] = formatDateTime(event.created_at);
                        break;
                    case 'modified_by':
                        row['Last Modified By'] = event.modified_by_name || '-';
                        break;
                    case 'modified_at':
                        row['Last Modified Date'] = formatDateTime(event.modified_at);
                        break;
                }
            });
            return row;
        });

        // Generate filename: time_tracking_<AccountName>_<dateFrom>_<dateTo>.xlsx
        const accountDisplayEl = document.getElementById('time-tracking-account-selected');
        const accountRaw = accountDisplayEl ? accountDisplayEl.textContent.trim() : '';
        const accountPart = (accountRaw && !/^\d+ accounts/.test(accountRaw))
            ? accountRaw.replace(/[^a-zA-Z0-9æøåÆØÅ]+/g, '_').replace(/^_|_$/g, '')
            : 'All';
        const dateFrom = timeTrackingState.filters.dateFrom || '';
        const dateTo = timeTrackingState.filters.dateTo || '';
        const datePart = [dateFrom, dateTo].filter(Boolean).join('_');
        const filename = `Tidsregistrering_${accountPart}${datePart ? '_' + datePart : ''}.xlsx`;

        exportToExcel(rows, filename);
        closeModal();
        showSuccess(`Exported ${rows.length} time tracking events successfully`);
    })
    .catch(err => {
        if (exportBtn && originalBtnHTML) {
            exportBtn.disabled = false;
            exportBtn.innerHTML = originalBtnHTML;
        }
        showError(err.message || 'Export failed');
    });
}

/**
 * Handle clicking on unknown user - try to resolve or show card ID
 */
window.handleUnknownUser = async function(eventId, cardId) {
    try {
        // First, try to resolve the unknown user
        const response = await apiCall(`/time-tracking/events/${eventId}/resolve-user`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });
        
        if (response.resolved) {
            // User was found and event updated
            showSuccess(`Card ${cardId} resolved to user: ${response.user_name}`);
            // Reload the time tracking list to show updated name
            await loadTimeTrackingData(true);
        } else {
            // Still unknown - show modal with card ID
            showUnknownCardModal(cardId);
        }
    } catch (error) {
        console.error('Error resolving unknown user:', error);
        // On error, just show the card ID
        showUnknownCardModal(cardId);
    }
};

/**
 * Show modal with unknown card ID and copy button
 */
function showUnknownCardModal(cardId) {
    const modal = document.getElementById('modal');
    
    modal.innerHTML = `
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
        <div class="fixed inset-0 z-10 overflow-y-auto">
            <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                    <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                        <div class="sm:flex sm:items-start">
                            <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                            </div>
                            <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                                <h3 class="text-lg font-semibold leading-6 text-gray-900 mb-4">
                                    ${t('time.unknownCard') || 'Unknown Card'}
                                </h3>
                                
                                <div class="space-y-4">
                                    <p class="text-sm text-gray-700">
                                        ${t('time.unknownCardMessage') || 'This card is not assigned to any user. Copy the card ID below and assign it to the correct user in the Users section.'}
                                    </p>
                                    
                                    <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <div class="flex items-center justify-between">
                                            <div>
                                                <label class="block text-xs font-medium text-gray-500 mb-1">
                                                    ${t('time.cardId') || 'Card ID'}
                                                </label>
                                                <code id="card-id-display" class="text-lg font-mono font-semibold text-gray-900 select-all">
                                                    ${cardId}
                                                </code>
                                            </div>
                                            <button
                                                onclick="copyCardIdToClipboard(event, '${cardId}')"
                                                class="ml-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors flex items-center space-x-2"
                                            >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                </svg>
                                                <span>${t('common.copy') || 'Copy'}</span>
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                        <div class="flex">
                                            <div class="flex-shrink-0">
                                                <svg class="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                                                </svg>
                                            </div>
                                            <div class="ml-3 text-sm text-blue-700">
                                                <p class="font-medium">${t('time.howToAssignCard') || 'How to assign this card:'}</p>
                                                <ol class="mt-2 list-decimal list-inside space-y-1">
                                                    <li>${t('time.copyCardId') || 'Copy the card ID above'}</li>
                                                    <li>${t('time.goToUsers') || 'Go to the Users section'}</li>
                                                    <li>${t('time.findUser') || 'Find the correct user'}</li>
                                                    <li>${t('time.pasteCardId') || 'Paste the card ID in the "NFC Card ID" field'}</li>
                                                    <li>${t('time.saveUser') || 'Save the user - the name will update automatically'}</li>
                                                </ol>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                        <button
                            onclick="closeModal()"
                            class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                        >
                            ${t('common.close') || 'Close'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;
    
    modal.classList.remove('hidden');
}

/**
 * Copy card ID to clipboard
 */
window.copyCardIdToClipboard = async function(event, cardId) {
    try {
        await navigator.clipboard.writeText(cardId);
        
        // Find the button and update its text
        const copyButton = event.target.closest('button');
        if (copyButton) {
            const originalHTML = copyButton.innerHTML;
            copyButton.innerHTML = `
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>${t('common.copied') || 'Copied'}</span>
            `;
            copyButton.classList.remove('bg-primary-600', 'hover:bg-primary-700');
            copyButton.classList.add('bg-green-600', 'hover:bg-green-700');
            
            // Reset after 2 seconds
            setTimeout(() => {
                copyButton.innerHTML = originalHTML;
                copyButton.classList.remove('bg-green-600', 'hover:bg-green-700');
                copyButton.classList.add('bg-primary-600', 'hover:bg-primary-700');
            }, 2000);
        }
        
        // Visual feedback on the code element
        const codeElement = document.getElementById('card-id-display');
        if (codeElement) {
            codeElement.classList.add('bg-green-100');
            setTimeout(() => {
                codeElement.classList.remove('bg-green-100');
            }, 2000);
        }
    } catch (error) {
        console.error('Failed to copy:', error);
        showError(t('time.copyFailed') || 'Failed to copy card ID');
    }
};

/**
 * Handle clicking on unknown location - try to resolve or show sensor ID
 */
window.handleUnknownLocation = async function(eventId, sensorId) {
    try {
        // First, try to resolve the unknown location
        const response = await apiCall(`/time-tracking/events/${eventId}/resolve-location`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });
        
        if (response.resolved) {
            // Location was found and event updated
            showSuccess(`Sensor ${sensorId} resolved to location: ${response.location_name}`);
            // Reload the time tracking list to show updated location
            await loadTimeTrackingData(true);
        } else {
            // Still unknown - show modal with sensor ID
            showUnknownSensorModal(sensorId);
        }
    } catch (error) {
        console.error('Error resolving unknown location:', error);
        showError('Failed to resolve location');
    }
};

/**
 * Show modal for unknown sensor with instructions
 */
function showUnknownSensorModal(sensorId) {
    const modal = document.getElementById('modal');
    if (!modal) return;
    
    modal.innerHTML = `
        <div class="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            
            <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                        <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <svg class="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                    </svg>
                                </div>
                                
                                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                                    <h3 class="text-base font-semibold leading-6 text-gray-900" id="modal-title">
                                        ${t('time.unknownLocation') || 'Unknown Location'}
                                    </h3>
                                    
                                    <div class="space-y-4">
                                        <p class="text-sm text-gray-700">
                                            ${t('time.unknownLocationMessage') || 'This sensor is not assigned to any location. Copy the sensor ID below and assign it in the Setup section.'}
                                        </p>
                                        
                                        <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                            <div class="flex items-center justify-between">
                                                <div>
                                                    <label class="block text-xs font-medium text-gray-500 mb-1">
                                                        ${t('time.sensorId') || 'Sensor ID'}
                                                    </label>
                                                    <code id="sensor-id-display" class="text-lg font-mono font-semibold text-gray-900 select-all">
                                                        ${sensorId}
                                                    </code>
                                                </div>
                                                <button
                                                    onclick="copySensorIdToClipboard(event, '${sensorId}')"
                                                    class="ml-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors flex items-center space-x-2"
                                                >
                                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                    </svg>
                                                    <span>${t('common.copy') || 'Copy'}</span>
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                            <h4 class="text-sm font-semibold text-blue-900 mb-2">
                                                ${t('time.howToAssignSensor') || 'How to assign this sensor:'}
                                            </h4>
                                            <ol class="list-decimal list-inside space-y-1 text-sm text-blue-800">
                                                <li>${t('time.copySensorId') || 'Copy the sensor ID above'}</li>
                                                <li>${t('time.goToSetup') || 'Go to the Setup section'}</li>
                                                <li>${t('time.findSensor') || 'Find this sensor (you can paste the ID in search)'}</li>
                                                <li>${t('time.assignLocation') || 'Assign the sensor to the correct location'}</li>
                                                <li>${t('time.saveLocation') || 'Save the configuration, then click the unknown location again to resolve'}</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            <button
                                onclick="closeModal()"
                                class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                            >
                                ${t('common.close') || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
    
    modal.classList.remove('hidden');
}

/**
 * Copy sensor ID to clipboard
 */
window.copySensorIdToClipboard = async function(event, sensorId) {
    try {
        await navigator.clipboard.writeText(sensorId);
        
        // Find the button and update its text
        const copyButton = event.target.closest('button');
        if (copyButton) {
            const originalHTML = copyButton.innerHTML;
            copyButton.innerHTML = `
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>${t('common.copied') || 'Copied'}</span>
            `;
            copyButton.classList.remove('bg-primary-600', 'hover:bg-primary-700');
            copyButton.classList.add('bg-green-600', 'hover:bg-green-700');
            
            // Reset after 2 seconds
            setTimeout(() => {
                copyButton.innerHTML = originalHTML;
                copyButton.classList.remove('bg-green-600', 'hover:bg-green-700');
                copyButton.classList.add('bg-primary-600', 'hover:bg-primary-700');
            }, 2000);
        }
        
        // Visual feedback on the code element
        const codeElement = document.getElementById('sensor-id-display');
        if (codeElement) {
            codeElement.classList.add('bg-green-100');
            setTimeout(() => {
                codeElement.classList.remove('bg-green-100');
            }, 2000);
        }
    } catch (error) {
        console.error('Failed to copy:', error);
        showError(t('time.copyFailed') || 'Failed to copy sensor ID');
    }
};

// Expose export functions to window
if (typeof window !== 'undefined') {
    window.showTimeTrackingExportModal = showTimeTrackingExportModal;
    window.exportTimeTrackingToExcel = exportTimeTrackingToExcel;
    window.handleUnknownUser = handleUnknownUser;
    window.generateTimeTrackingPDF = generateTimeTrackingPDF;
}

/**
 * Generate PDF report from time tracking data using Filter template
 */
async function generateTimeTrackingPDF() {
    try {
        const currentUser = getCurrentUser();
        if (!currentUser || !currentUser.account_id) {
            showError('User not authenticated');
            return;
        }

        // Check if a default template is set in account settings.
        // Wrapped in its own try/catch so a failure here still falls through
        // to the template-picker modal instead of silently aborting.
        let settingsResponse = null;
        try {
            settingsResponse = await apiCall(`/accounts/${currentUser.account_id}`, {
                headers: auth.getAuthHeaders()
            });
        } catch (e) {
            console.warn('[PDF] Failed to load account settings, falling back to template picker:', e);
        }

        const defaultTemplateId = settingsResponse?.time_tracking_report_template_id;

        if (defaultTemplateId) {
            // Default template configured — still let the user pick sort order
            showPdfSortPromptModal(async (sortBy) => {
                await generatePDFWithTemplate(defaultTemplateId, sortBy);
            });
        } else {
            // Show template selection modal (sort selector is embedded inside)
            await showTemplateSelectionModal();
        }
    } catch (error) {
        console.error('Failed to generate PDF:', error);
        showError('Failed to generate PDF report');
    }
}

/**
 * Show modal to select a Filter template
 */
async function showTemplateSelectionModal() {
    console.log('[PDF] showTemplateSelectionModal called');
    try {
        const currentUser = getCurrentUser();
        
        // Load Filter templates only
        console.log('[PDF] Loading templates for account', currentUser.account_id);
        const templates = await apiCall(`/report-templates/templates?account_id=${currentUser.account_id}`, {
            headers: auth.getAuthHeaders()
        });
        console.log('[PDF] Templates loaded, count:', Array.isArray(templates) ? templates.length : '(not array)', templates);

        const filterTemplates = templates.filter(t => t.report_type === 'filter' && t.is_released);
        console.log('[PDF] filterTemplates (report_type=filter && is_released) count:', filterTemplates.length);

        if (filterTemplates.length === 0) {
            showError('No Filter report templates available. Please create and release a Filter template first.');
            return;
        }
        
        const modal = document.createElement('div');
        modal.id = 'template-selection-modal';
        modal.className = 'fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50';
        
        modal.innerHTML = `
            <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
                <div class="px-6 py-4 border-b border-gray-200">
                    <h3 class="text-lg font-medium text-gray-900">Select Report Template</h3>
                    <p class="mt-1 text-sm text-gray-500">Choose a template to generate the PDF report</p>
                </div>
                
                <div class="p-6">
                    ${renderSortControlsHtml('pdf-sort', 'Sort events by')}
                    <div class="space-y-2">
                        ${filterTemplates.map(template => `
                            <button onclick="selectTemplateAndGenerate('${template._id}')"
                                    class="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-primary-50 hover:border-primary-500 transition-all">
                                <div class="flex items-center">
                                    <svg class="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                    </svg>
                                    <div class="flex-1">
                                        <div class="font-medium text-gray-900">${escapeHtml(template.name)}</div>
                                        ${template.description ? `<div class="text-sm text-gray-500 mt-1">${escapeHtml(template.description)}</div>` : ''}
                                    </div>
                                </div>
                            </button>
                        `).join('')}
                    </div>
                </div>
                
                <div class="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end">
                    <button onclick="closeTemplateSelectionModal()" 
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        Cancel
                    </button>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
        
        // Close on background click
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                closeTemplateSelectionModal();
            }
        });
        
    } catch (error) {
        console.error('[PDF] Failed to load templates:', error);
        showError('Failed to load templates');
    }
}

/**
 * Close template selection modal
 */
window.closeTemplateSelectionModal = function() {
    const modal = document.getElementById('template-selection-modal');
    if (modal) {
        modal.remove();
    }
};

/**
 * Small modal that only asks the user how to sort the PDF events.
 * Used when a default PDF template is configured (so the full template
 * picker is skipped) but we still want the customer to choose sort order.
 */
function showPdfSortPromptModal(onConfirm) {
    const modal = document.createElement('div');
    modal.id = 'pdf-sort-prompt-modal';
    modal.className = 'fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50';
    modal.innerHTML = `
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div class="px-6 py-4 border-b border-gray-200">
                <h3 class="text-lg font-medium text-gray-900">Generate PDF Report</h3>
                <p class="mt-1 text-sm text-gray-500">Choose how the events should be sorted</p>
            </div>
            <div class="p-6">
                ${renderSortControlsHtml('pdf-sort', 'Sort events by')}
            </div>
            <div class="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-2">
                <button id="pdf-sort-prompt-cancel"
                        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                    Cancel
                </button>
                <button id="pdf-sort-prompt-confirm"
                        class="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700">
                    Generate
                </button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    const close = () => modal.remove();
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
    modal.querySelector('#pdf-sort-prompt-cancel').addEventListener('click', close);
    modal.querySelector('#pdf-sort-prompt-confirm').addEventListener('click', () => {
        const sortBy = readSortControls('pdf-sort', modal);
        close();
        if (typeof onConfirm === 'function') onConfirm(sortBy);
    });
}

/**
 * Fetch all time tracking events matching current filters (no pagination)
 */
async function fetchAllTimeTrackingEvents() {
    const params = new URLSearchParams({
        skip: '0',
        limit: '500' // Maximum 500 events for PDF reports
    });

    // Add filters (same as loadTimeTrackingData)
    if (timeTrackingState.filters.dateFrom) {
        params.append('start_date', localDateToStartISO(timeTrackingState.filters.dateFrom));
    }
    if (timeTrackingState.filters.dateTo) {
        params.append('end_date', localDateToEndISO(timeTrackingState.filters.dateTo));
    }
    if (timeTrackingState.filters.status) {
        params.append('status', timeTrackingState.filters.status);
    }
    if (selectedTimeTrackingUsers.size > 0) {
        selectedTimeTrackingUsers.forEach((_name, userId) => {
            params.append('user_id', userId);
        });
    }
    
    // Add account filter if any accounts are selected
    if (selectedTimeTrackingAccounts.size > 0) {
        const accountIds = [];
        selectedTimeTrackingAccounts.forEach(accountId => {
            const idsWithDescendants = getAllAccountIdsIncludingDescendants(accountId);
            accountIds.push(...idsWithDescendants);
        });
        // Remove duplicates
        const uniqueAccountIds = [...new Set(accountIds)];
        uniqueAccountIds.forEach(id => params.append('account_id', id));
    }
    
    // Add location filter if any locations are selected
    if (selectedTimeTrackingLocations.size > 0) {
        selectedTimeTrackingLocations.forEach(locationId => {
            params.append('location_id', locationId);
        });
    }

    // Unknown location filter overrides account/location filters
    if (filterUnknownLocation) {
        params.append('unknown_location', 'true');
    }

    // Unknown user filter
    if (filterUnknownUser) {
        params.append('unknown_user', 'true');
    }

    console.log('Fetching ALL time tracking events for PDF with params:', params.toString());
    const response = await fetch(`${API_BASE_URL}/time-tracking/events/?${params}`, {
        headers: {
            'Authorization': `Bearer ${auth.getToken()}`
        }
    });

    if (!response.ok) {
        throw new Error('Failed to load time tracking events');
    }

    const data = await response.json();
    return data.events || [];
}

/**
 * Select template and generate PDF
 */
window.selectTemplateAndGenerate = async function(templateId) {
    // Read sort selection BEFORE closing the modal (otherwise the selects are gone)
    const sortBy = readSortControls('pdf-sort');
    closeTemplateSelectionModal();
    await generatePDFWithTemplate(templateId, sortBy);
};

/**
 * Generate PDF with selected template
 */
async function generatePDFWithTemplate(templateId, sortBy) {
    try {
        // Get current filter state
        const filterContext = {
            date_from: timeTrackingState.filters.dateFrom || null,
            date_to: timeTrackingState.filters.dateTo || null,
            location_ids: timeTrackingState.selectedLocations || [],
            asset_ids: timeTrackingState.selectedAssets || [],
            account_ids: timeTrackingState.selectedAccounts || []
        };
        
        // Show loading modal
        const loadingModal = document.createElement('div');
        loadingModal.id = 'pdf-loading-modal';
        loadingModal.className = 'fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50';
        loadingModal.innerHTML = `
            <div class="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4">
                <div class="flex items-center justify-center mb-4">
                    <svg class="animate-spin h-12 w-12 text-primary-600" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
                <h3 class="text-lg font-medium text-gray-900 text-center mb-2">Generating PDF Report</h3>
                <p class="text-sm text-gray-500 text-center">Please wait while we prepare your report...</p>
            </div>
        `;
        document.body.appendChild(loadingModal);
        
        // Check total event count before fetching - limit to 500 events
        const countParams = new URLSearchParams({ skip: '0', limit: '1' });
        if (timeTrackingState.filters.dateFrom) countParams.append('start_date', localDateToStartISO(timeTrackingState.filters.dateFrom));
        if (timeTrackingState.filters.dateTo) countParams.append('end_date', localDateToEndISO(timeTrackingState.filters.dateTo));
        if (timeTrackingState.filters.status) countParams.append('status', timeTrackingState.filters.status);
        if (selectedTimeTrackingUsers.size > 0) {
            selectedTimeTrackingUsers.forEach((_name, userId) => {
                countParams.append('user_id', userId);
            });
        }
        if (selectedTimeTrackingAccounts.size > 0) {
            const accountIds = [];
            selectedTimeTrackingAccounts.forEach(accountId => {
                const idsWithDescendants = getAllAccountIdsIncludingDescendants(accountId);
                accountIds.push(...idsWithDescendants);
            });
            const uniqueAccountIds = [...new Set(accountIds)];
            uniqueAccountIds.forEach(id => countParams.append('account_id', id));
        }
        if (selectedTimeTrackingLocations.size > 0) {
            selectedTimeTrackingLocations.forEach(locationId => {
                countParams.append('location_id', locationId);
            });
        }
        
        const countResponse = await fetch(`${API_BASE_URL}/time-tracking/events/?${countParams}`, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        if (countResponse.ok) {
            const countData = await countResponse.json();
            if (countData.total > 500) {
                loadingModal.remove();
                showError(`Too many events (${countData.total}) for PDF generation. Maximum is 500 events. Please narrow your date range or filters.`);
                return;
            }
        }
        
        // Get template structure
        const response = await apiCall(`/report-templates/templates/${templateId}`, {
            headers: auth.getAuthHeaders()
        });
        
        // Fetch template elements
        const elementsResponse = await apiCall(`/report-templates/templates/${templateId}/elements`, {
            headers: auth.getAuthHeaders()
        });
        
        // Organize elements by page
        const frontpageElements = elementsResponse.filter(e => e.page === 'frontpage').sort((a, b) => a.position - b.position);
        const resultsElements = elementsResponse.filter(e => e.page === 'results').sort((a, b) => a.position - b.position);
        
        response.frontpage = { elements: frontpageElements };
        response.results = { elements: resultsElements };
        
        // Fetch ALL events matching current filters (not just paginated data)
        const events = await fetchAllTimeTrackingEvents();
        
        console.log('Fetched', events.length, 'events for PDF');
        
        // Apply user-selected sort (defaults to date desc / no grouping)
        const sortedEvents = sortTimeTrackingEvents(events, sortBy);

        // Render template with data and generate PDF
        await renderAndDownloadPDF(response, filterContext, sortedEvents);
        
        // Remove loading modal
        loadingModal.remove();
        showSuccess('PDF downloaded successfully');
        
    } catch (error) {
        console.error('Failed to generate PDF:', error);
        const modal = document.getElementById('pdf-loading-modal');
        if (modal) modal.remove();
        showError('Failed to generate PDF');
    }
}

/**
 * Render template with data and generate PDF (multi-page support).
 *
 * Strategy:
 *  - Cover/branding pages (frontpage + any non-table results elements) are
 *    rasterized via html2canvas — this is fine, they're usually one page.
 *  - The events table is rendered as a native (vector) PDF table via
 *    jspdf-autotable. autoTable paginates dynamically based on actual row
 *    height, so a wrapping long username just makes that row taller and
 *    the next row flows onto the next page — no more "1-row leftover" pages.
 *    Vector tables are also ~5–20× faster than html2canvas and the resulting
 *    PDF is small and searchable.
 */
async function renderAndDownloadPDF(template, filterContext, events) {
    // Split the template into (a) HTML pages for the cover/branding portion
    // and (b) a flag indicating whether the events table should be appended.
    const { coverPages, hasEventsTable, tableElement } = renderTemplateForPDF(template, filterContext, events);

    console.log('Cover pages:', coverPages.length, 'hasEventsTable:', hasEventsTable);

    const { jsPDF } = window.jspdf;
    const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
    });

    const pdfWidth = 297; // A4 landscape width in mm
    const pdfHeight = 210; // A4 landscape height in mm
    let isFirstPage = true;

    // ---- 1) Rasterize cover/branding pages with html2canvas ----
    for (let pageIndex = 0; pageIndex < coverPages.length; pageIndex++) {
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        container.style.width = '794px';
        document.body.appendChild(container);

        try {
            const page = document.createElement('div');
            page.style.backgroundColor = '#ffffff';
            page.style.padding = '30px';
            page.style.width = '794px';
            page.innerHTML = coverPages[pageIndex];
            container.appendChild(page);

            // Small wait for fonts/images to settle
            await new Promise(resolve => setTimeout(resolve, 50));

            // scale: 1.5 is a good speed/quality trade-off — text remains crisp
            // on screen and in print, but rasterization is ~2x faster than scale: 2.
            const canvas = await html2canvas(page, {
                scale: 1.5,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff'
            });

            const imgWidth = pdfWidth;
            const imgHeight = (canvas.height * pdfWidth) / canvas.width;

            if (imgHeight <= pdfHeight) {
                // Fits on one PDF page
                const imgData = canvas.toDataURL('image/jpeg', 0.85);
                if (!isFirstPage) pdf.addPage();
                isFirstPage = false;
                pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
            } else {
                // Cover content is taller than one PDF page — split the canvas
                // by pixel slices. This rarely happens for cover pages.
                const topMargin = 5;
                let remainingHeight = canvas.height;
                let currentY = 0;
                let sliceIndex = 0;

                while (remainingHeight > 0) {
                    if (!isFirstPage) pdf.addPage();
                    isFirstPage = false;

                    const availableHeight = sliceIndex === 0 ? pdfHeight : (pdfHeight - topMargin);
                    const pixelsForThisPage = Math.floor((availableHeight * canvas.width) / pdfWidth);
                    const actualPixels = Math.min(pixelsForThisPage, remainingHeight);

                    const sectionCanvas = document.createElement('canvas');
                    sectionCanvas.width = canvas.width;
                    sectionCanvas.height = actualPixels;
                    const ctx = sectionCanvas.getContext('2d');
                    ctx.drawImage(canvas, 0, currentY, canvas.width, actualPixels, 0, 0, canvas.width, actualPixels);

                    const sectionData = sectionCanvas.toDataURL('image/jpeg', 0.85);
                    const sectionImgHeight = (actualPixels * pdfWidth) / canvas.width;
                    const yPosition = sliceIndex > 0 ? topMargin : 0;
                    pdf.addImage(sectionData, 'JPEG', 0, yPosition, imgWidth, sectionImgHeight);

                    currentY += actualPixels;
                    remainingHeight -= actualPixels;
                    sliceIndex++;
                }
            }
        } finally {
            container.remove();
        }
    }

    // ---- 2) Append the events table as native PDF text using autoTable ----
    if (hasEventsTable) {
        if (typeof pdf.autoTable !== 'function') {
            console.error('jspdf-autotable plugin not loaded — falling back to rasterized table');
            // Fallback: rasterize the table the old way so the PDF still works.
            await appendRasterizedTable(pdf, tableElement, events, isFirstPage, pdfWidth, pdfHeight);
        } else {
            const introText = tableElement?.intro_text || tableElement?.introText;
            renderEventsAutoTable(pdf, events, introText, isFirstPage);
        }
    }

    // Filename: Tidsregistrering_<AccountName>_<dateFrom>_<dateTo>.pdf
    const accountDisplayEl = document.getElementById('time-tracking-account-selected');
    const accountRaw = accountDisplayEl ? accountDisplayEl.textContent.trim() : '';
    const accountPart = (accountRaw && !/^\d+ accounts/.test(accountRaw))
        ? accountRaw.replace(/[^a-zA-Z0-9æøåÆØÅ]+/g, '_').replace(/^_|_$/g, '')
        : 'All';
    const pdfDateFrom = filterContext.date_from || '';
    const pdfDateTo = filterContext.date_to || '';
    const datePart = [pdfDateFrom, pdfDateTo].filter(Boolean).join('_');
    const filename = `Tidsregistrering_${accountPart}${datePart ? '_' + datePart : ''}.pdf`;
    pdf.save(filename);
}

/**
 * Render the events table as a native (vector) PDF table.
 * Uses jspdf-autotable: dynamic row-height pagination, repeated header row,
 * alternating row colors — same visual style as the previous rasterized table.
 */
function renderEventsAutoTable(pdf, events, introText, isFirstPage) {
    const pdfWidth = 297;
    const pdfHeight = 210;

    if (!isFirstPage) pdf.addPage();

    let startY = 12;
    if (introText) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(17, 24, 39); // gray-900
        pdf.text(introText, 10, startY);
        startY += 6;
    }

    const head = [[
        t('time.date') || 'Date',
        t('time.employee') || 'Employee',
        t('time.location') || 'Location',
        t('time.checkIn') || 'Check In',
        t('time.checkOut') || 'Check Out',
        t('time.totalTime') || 'Total time',
    ]];

    const body = (events || []).map(event => {
        const eventDate = event.check_in_time
            ? new Date(event.check_in_time).toLocaleDateString('da-DK', { year: 'numeric', month: '2-digit', day: '2-digit' })
            : '-';
        const checkIn = event.check_in_time
            ? new Date(event.check_in_time).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })
            : '-';
        const checkOut = event.check_out_time
            ? new Date(event.check_out_time).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })
            : '-';
        let totalTime = '-';
        if (event.duration_minutes !== null && event.duration_minutes !== undefined) {
            const h = Math.floor(event.duration_minutes / 60);
            const m = event.duration_minutes % 60;
            totalTime = `${h}h ${m}m`;
        }
        return [
            eventDate,
            event.user_name || 'Unknown',
            event.location_name || 'Unknown',
            checkIn,
            checkOut,
            totalTime,
        ];
    });

    pdf.autoTable({
        head,
        body,
        startY,
        margin: { left: 10, right: 10, bottom: 12 },
        styles: {
            fontSize: 9,
            cellPadding: { top: 1.5, right: 2, bottom: 1.5, left: 2 },
            overflow: 'linebreak',
            valign: 'middle',
            textColor: [0, 0, 0],
        },
        headStyles: {
            fillColor: [156, 163, 175], // gray-400 (matches the previous header)
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'left',
        },
        alternateRowStyles: {
            fillColor: [229, 231, 235], // gray-200 (matches previous striping)
        },
        // Column widths tuned for A4 landscape (~277mm usable)
        columnStyles: {
            0: { cellWidth: 22 },  // Date
            1: { cellWidth: 'auto' }, // Employee
            2: { cellWidth: 'auto' }, // Location
            3: { cellWidth: 20 },  // Check In
            4: { cellWidth: 20 },  // Check Out
            5: { cellWidth: 22 },  // Total time
        },
        didDrawPage: (data) => {
            // Page footer with page number
            const pageCount = pdf.internal.getNumberOfPages();
            const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(107, 114, 128); // gray-500
            pdf.text(`${currentPage} / ${pageCount}`, pdfWidth - 10, pdfHeight - 5, { align: 'right' });
        },
    });
}

/**
 * Fallback path: rasterize the events table the old way when jspdf-autotable
 * isn't available for any reason (offline / CDN blocked / etc.).
 */
async function appendRasterizedTable(pdf, tableElement, events, isFirstPage, pdfWidth, pdfHeight) {
    const ROWS_PER_PAGE = 22;
    const chunks = [];
    for (let i = 0; i < (events || []).length; i += ROWS_PER_PAGE) {
        chunks.push(events.slice(i, i + ROWS_PER_PAGE));
    }
    for (let ci = 0; ci < chunks.length; ci++) {
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        container.style.width = '794px';
        document.body.appendChild(container);
        try {
            const page = document.createElement('div');
            page.style.backgroundColor = '#ffffff';
            page.style.padding = '30px';
            page.style.width = '794px';
            page.innerHTML = renderFilteredDataTable(tableElement, chunks[ci]);
            container.appendChild(page);
            await new Promise(r => setTimeout(r, 50));
            const canvas = await html2canvas(page, { scale: 1.5, useCORS: true, logging: false, backgroundColor: '#ffffff' });
            const imgWidth = pdfWidth;
            const imgHeight = (canvas.height * pdfWidth) / canvas.width;
            if (!isFirstPage) pdf.addPage();
            isFirstPage = false;
            pdf.addImage(canvas.toDataURL('image/jpeg', 0.85), 'JPEG', 0, 0, imgWidth, Math.min(imgHeight, pdfHeight));
        } finally {
            container.remove();
        }
    }
}

/**
 * Render template HTML for PDF generation.
 * Returns:
 *  - coverPages    : array of HTML strings for the cover/branding pages
 *                    (frontpage + any non-table results elements).
 *  - hasEventsTable: true if a `filtered-data` element exists in results.
 *  - tableElement  : the `filtered-data` element (used to read intro_text /
 *                    for the rasterized fallback).
 *
 * The events table itself is NOT pre-paginated into HTML chunks anymore —
 * jspdf-autotable handles row-by-row pagination natively.
 */
function renderTemplateForPDF(template, filterContext, events) {
    const coverPages = [];
    let hasEventsTable = false;
    let tableElement = null;

    // Frontpage as the first cover page
    if (template.frontpage && template.frontpage.elements) {
        let frontpageHtml = '';
        template.frontpage.elements.forEach(element => {
            frontpageHtml += renderTemplateElement(element, filterContext, events);
        });
        if (frontpageHtml) coverPages.push(frontpageHtml);
    }

    // Results page: collect non-table elements as a single cover page,
    // and remember that a table element exists so we can render it via autoTable.
    if (template.results && template.results.elements) {
        let resultsHtml = '';
        template.results.elements.forEach(element => {
            const elementType = element.element_type || element.type;
            if (elementType === 'filtered-data') {
                hasEventsTable = true;
                tableElement = element;
                // Skip — the table is rendered natively via autoTable later
            } else {
                resultsHtml += renderTemplateElement(element, filterContext, events);
            }
        });
        if (resultsHtml) coverPages.push(resultsHtml);
    }

    if (coverPages.length === 0 && !hasEventsTable) {
        coverPages.push('<div class="p-4"><h1 class="text-xl font-bold mb-4">Time Tracking Report</h1><p>Template has no elements configured.</p></div>');
    }

    return { coverPages, hasEventsTable, tableElement };
}

/**
 * Render individual template element
 */
function renderTemplateElement(element, filterContext, events) {
    const elementType = element.element_type || element.type;
    const introText = element.intro_text || element.introText;
    
    switch (elementType) {
        case 'image':
            if (element.static_data && element.static_data.imageData) {
                try {
                    // Alignment can be at element level or in static_data
                    const alignment = element.alignment || element.static_data.alignment || 'center';
                    console.log('Image alignment:', alignment, 'Element:', element);
                    let alignStyle = 'margin: 0 auto;'; // default center
                    let textAlign = 'text-center';
                    
                    if (alignment === 'left') {
                        alignStyle = 'margin: 0;';
                        textAlign = 'text-left';
                    } else if (alignment === 'right') {
                        alignStyle = 'margin: 0 0 0 auto;';
                        textAlign = 'text-right';
                    }
                    
                    return `
                        <div class="mb-4 ${textAlign}">
                            <img src="${element.static_data.imageData}" alt="" style="max-width: 200px; max-height: 100px; ${alignStyle}" />
                        </div>
                    `;
                } catch (error) {
                    console.error('Error rendering image:', error);
                    return `
                        <div class="mb-4 text-center">
                            <img src="${element.static_data.imageData}" alt="" style="max-width: 200px; max-height: 100px; margin: 0 auto;" />
                        </div>
                    `;
                }
            }
            return '';
        
        case 'filter-date-range':
            const dateFrom = filterContext.date_from ? new Date(filterContext.date_from).toLocaleDateString('da-DK') : 'N/A';
            const dateTo = filterContext.date_to ? new Date(filterContext.date_to).toLocaleDateString('da-DK') : 'N/A';
            return `
                <div class="mb-4">
                    <h3 class="font-semibold text-gray-900">${introText || 'Date Range'}</h3>
                    <p class="text-gray-700">${dateFrom} - ${dateTo}</p>
                </div>
            `;
        
        case 'filter-location':
            return `
                <div class="mb-4">
                    <h3 class="font-semibold text-gray-900">${introText || 'Locations'}</h3>
                    <p class="text-gray-700">${filterContext.location_ids && filterContext.location_ids.length ? filterContext.location_ids.length + ' selected' : 'All'}</p>
                </div>
            `;
        
        case 'filter-asset':
            return `
                <div class="mb-4">
                    <h3 class="font-semibold text-gray-900">${introText || 'Assets'}</h3>
                    <p class="text-gray-700">${filterContext.asset_ids && filterContext.asset_ids.length ? filterContext.asset_ids.length + ' selected' : 'All'}</p>
                </div>
            `;
        
        case 'filter-account':
            return `
                <div class="mb-4">
                    <h3 class="font-semibold text-gray-900">${introText || 'Accounts'}</h3>
                    <p class="text-gray-700">${filterContext.account_ids && filterContext.account_ids.length ? filterContext.account_ids.length + ' selected' : 'All'}</p>
                </div>
            `;
        
        case 'filtered-data':
            const tableHtml = renderFilteredDataTable(element, events);
            console.log('filtered-data case - table HTML length:', tableHtml.length);
            return tableHtml;
        
        default:
            return '';
    }
}

/**
 * Render filtered data table
 */
function renderFilteredDataTable(element, events) {
    const introText = element.intro_text || element.introText;
    let html = '';
    
    if (introText) {
        html += `<h3 class="font-semibold text-gray-900 mb-2">${introText}</h3>`;
    }
    
    console.log('Rendering table with', events.length, 'events');
    if (events.length > 0) {
        console.log('First event structure:', events[0]);
        console.log('Event fields:', Object.keys(events[0]));
    }
    
    html += `
        <table class="min-w-full" style="width: 100%; border-collapse: collapse; font-size: 11px; line-height: 1.3;">
            <thead style="background-color: #9CA3AF;">
                <tr>
                    <th class="px-2 text-left text-xs font-medium text-white" style="vertical-align: middle; padding-top: 3px; padding-bottom: 9px;">${t('time.date')}</th>
                    <th class="px-2 text-left text-xs font-medium text-white" style="vertical-align: middle; padding-top: 3px; padding-bottom: 9px;">${t('time.employee')}</th>
                    <th class="px-2 text-left text-xs font-medium text-white" style="vertical-align: middle; padding-top: 3px; padding-bottom: 9px;">${t('time.location')}</th>
                    <th class="px-2 text-left text-xs font-medium text-white" style="vertical-align: middle; padding-top: 3px; padding-bottom: 9px;">${t('time.checkIn')}</th>
                    <th class="px-2 text-left text-xs font-medium text-white" style="vertical-align: middle; padding-top: 3px; padding-bottom: 9px;">${t('time.checkOut')}</th>
                    <th class="px-2 text-left text-xs font-medium text-white" style="vertical-align: middle; padding-top: 3px; padding-bottom: 9px;">${t('time.totalTime')}</th>
                </tr>
            </thead>
            <tbody class="bg-white">
    `;
    
    if (events && events.length > 0) {
        events.forEach((event, index) => {
            // Use the actual field names from the event
            const userName = event.user_name || 'Unknown';
            const locationName = event.location_name || 'Unknown';
            
            // Format date separately (first column)
            const eventDate = event.check_in_time ? new Date(event.check_in_time).toLocaleDateString('da-DK', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }) : '-';
            
            // Format check-in and check-out as time only (HH:MM)
            const checkInTime = event.check_in_time ? new Date(event.check_in_time).toLocaleTimeString('da-DK', {
                hour: '2-digit',
                minute: '2-digit'
            }) : '-';
            const checkOutTime = event.check_out_time ? new Date(event.check_out_time).toLocaleTimeString('da-DK', {
                hour: '2-digit',
                minute: '2-digit'
            }) : '-';
            
            // Format duration_minutes as "Xh Ym"
            let totalTime = '-';
            if (event.duration_minutes !== null && event.duration_minutes !== undefined) {
                const hours = Math.floor(event.duration_minutes / 60);
                const minutes = event.duration_minutes % 60;
                totalTime = `${hours}h ${minutes}m`;
            }
            
            // Alternating row colors - light gray (#E5E7EB) and white
            const bgColor = index % 2 === 0 ? '#FFFFFF' : '#E5E7EB';
            
            html += `
                <tr style="background-color: ${bgColor};">
                    <td class="px-2" style="color: #000000; vertical-align: middle; padding-top: 1px; padding-bottom: 6px;">${eventDate}</td>
                    <td class="px-2" style="color: #000000; vertical-align: middle; padding-top: 1px; padding-bottom: 6px;">${userName}</td>
                    <td class="px-2" style="color: #000000; vertical-align: middle; padding-top: 1px; padding-bottom: 6px;">${locationName}</td>
                    <td class="px-2" style="color: #000000; vertical-align: middle; padding-top: 1px; padding-bottom: 6px;">${checkInTime}</td>
                    <td class="px-2" style="color: #000000; vertical-align: middle; padding-top: 1px; padding-bottom: 6px;">${checkOutTime}</td>
                    <td class="px-2" style="color: #000000; vertical-align: middle; padding-top: 1px; padding-bottom: 6px;">${totalTime}</td>
                </tr>
            `;
        });
    } else {
        html += `
            <tr>
                <td colspan="6" class="px-2 py-2 text-gray-500 text-center">${t('time.noEventsFound')}</td>
            </tr>
        `;
    }
    
    html += `
            </tbody>
        </table>
    `;
    
    console.log('Generated table HTML length:', html.length);
    console.log('Table HTML preview:', html.substring(0, 200));
    
    return html;
}

/**
 * Helper function to escape HTML
 */
function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

export default {
    showTimeTracking,
    loadTimeTrackingData,
    filterTimeTracking,
    clearTimeTrackingFilters
};
